import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  TablePagination,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Select,
  FormControl,
  Tooltip,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Archive as ArchiveIcon,
  DirectionsCar as CarIcon,
  ArrowBack as ArrowBackIcon,
  Unarchive as UnarchiveIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  ImageSearch as ImageSearchIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  LocalShipping as TruckIcon,
  Speed as SpeedIcon,
  CameraAlt as CameraIcon,
  Lightbulb as LightbulbIcon,
  MeetingRoom as DoorIcon,
  DriveEta as FrontCarIcon,
  ArrowBackIosNew as BackIcon,
  AirlineSeatReclineNormal as SeatIcon,
  Build as EngineIcon,
  Warning as WarningIcon,
  Folder as FolderIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Build as BuildIcon,
  QrCode2 as QrCodeIcon,
} from "@mui/icons-material";
import axios, { AxiosError } from "axios";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { API_URL } from "../utils/api-config";
import * as XLSX from "xlsx";
import { Upload as UploadIcon } from "@mui/icons-material";
import {
  vehicleTemplates,
  categoryConfigs,
  type VehicleTemplate,
  type CategoryConfig,
} from "../data/vehicleTemplates";
import VehicleQRCode from "../components/VehicleQRCode";
import {
  getBrandsByVehicleType,
  getModelsByBrand,
} from "../data/vehicleBrands";

type FuelType =
  | "diesel"
  | "essence"
  | "electrique"
  | "gnl"
  | "gnc"
  | "gpl"
  | "hybride_gnc"
  | "hybride_essence"
  | "hybride_diesel"
  | "biocarburant"
  | "sans_carburant"
  | "gaz"
  | "hybride";
type FrigoType = "gasoil" | "electrique";

interface Vehicle {
  _id?: string;
  plate: string;
  parkNumber?: string; // Numéro du parc
  brand: string;
  model: string;
  year: string;
  mileage: string;
  vehicleType?: VehicleType;
  fuelType?: FuelType;
  ownershipStatus: "leasing" | "location" | "propriétaire";
  leasingEndDate?: string;
  nextMaintenanceDate?: string;

  // Dimensions & capacités utiles
  payloadCapacity?: number; // Charge utile (kg)
  volumeCapacity?: number; // Capacité volume (m³)
  innerLength?: number; // Longueur utile intérieure (m)
  innerWidth?: number; // Largeur utile intérieure (m)
  innerHeight?: number; // Hauteur utile intérieure (m)
  compartmentsCount?: number; // Nombre de compartiments
  palletsEurope?: number; // Capacité palettes Europe
  palletsISO?: number; // Capacité palettes ISO

  // Équipements
  hasTailgate?: boolean; // A un hayon
  tailgateCapacity?: number; // Capacité hayon (kg)
  hasCrane?: boolean; // A une grue
  craneCapacity?: number; // Capacité grue (kg)

  // Consommation
  consumptionElectricConstructor?: number;
  consumptionElectricReal?: number;
  consumptionGasConstructor?: number;
  consumptionGasReal?: number;
  consumptionFuelConstructor?: number;
  consumptionFuelReal?: number;

  // Réservoirs
  fuelTankCapacity?: number;
  adBlueTankCapacity?: number;
  adBlueConsumption?: number;
  adBlueRatio?: number;

  // Groupe frigorifique
  frigoType?: FrigoType;
  frigoConsumptionGasoilConstructor?: number;
  frigoConsumptionGasoilReal?: number;
  frigoTankCapacity?: number;
  frigoConsumptionElectricConstructor?: number;
  frigoConsumptionElectricReal?: number;
  frigoBatteryAutonomy?: number;

  // Contrôles VUL
  technicalInspectionDate?: string;
  firstTechnicalInspectionDate?: string;
  pollutionControlDate?: string;
  firstPollutionControlDate?: string;

  // Contrôles équipements
  tailgateInspectionDate?: string;
  firstTailgateInspectionDate?: string;
  craneInspectionDate?: string;
  firstCraneInspectionDate?: string;
  extinguisherInspectionDate?: string;

  // Contrôles frigorifique
  atpCertificateDate?: string;
  temperatureRecorderDate?: string;

  // Contrôles PL
  technicalInspectionPLDate?: string;
  tachographInspectionDate?: string;
  firstTachographInspectionDate?: string;

  // Contrôles ADR/Citerne
  adrDate?: string;
  adrIntermediateDate?: string;
  adrPeriodicDate?: string;
  adrHosesDate?: string;

  // Dates & financier
  firstRegistrationDate?: string;
  acquisitionDate?: string;
  registrationFees?: number;
  monthlyPaymentAmount?: number;
  firstPaymentDate?: string;
  lastPaymentDate?: string;
  annualInsuranceAmount?: number;
  monthlyGeolocationAmount?: number;
  wheelsCount?: number;
  axlesCount?: number;
  axleTaxAmount?: number;

  // Équipements supplémentaires
  hasChronotachygraphe?: string;
  hasSpeedLimiter?: string;
  hasFrigoGroup?: string;
  extinguisherCount?: number;

  // Groupe frigo complémentaire
  frigoFuelType?: string;
  frigoFuelTankCapacity?: number;
  frigoConsumptionLH?: number;
  frigoConsumptionL100?: number;
  frigoAutonomyHours?: number;

  // Contrôles supplémentaires
  frigoGroupInspectionDate?: string;
  speedLimiterInspectionDate?: string;

  // Autres
  weighingInstrumentsDate?: string;
  otherEquipmentInspectionDate?: string;
  technicalInspectionType?: string;
  operationalState: OperationalState;
  archiveReason?: string;

  // Garage en cours
  currentGarage?: {
    garageId: string;
    garageName: string;
    garageAddress?: string;
    garagePhone?: string;
    entryDate: string;
    estimatedExitDate?: string;
    maintenanceReason?: string;
  };

  // Photos d'inspection
  inspectionPhotos?: {
    driverSide?: string;
    passengerSide?: string;
    front?: string;
    back?: string;
    interior?: string;
    engine?: string;
    damage?: string;
  };

  // Assignation
  hasAssignment?: boolean;
  currentAssignment?: {
    id?: string;
    date?: string;
    time?: string;
    driverId?: string;
    driverFirstName?: string;
    driverLastName?: string;
  };
  driver?: {
    firstName?: string;
    lastName?: string;
  };
}

interface Garage {
  _id: string;
  nomCommercial: string;
  adresse: string;
  ville: string;
  telephone: string;
  tauxHoraireActuel: number;
  scoreClassement?: "A" | "B" | "C";
}

type OperationalState =
  | "disponible"
  | "en_service"
  | "maintenance"
  | "parking"
  | "archivé";

const operationalStateOptions = [
  "disponible",
  "en_service",
  "maintenance",
  "parking",
];

const operationalStateLabels: Record<OperationalState, string> = {
  disponible: "Disponible",
  en_service: "En service",
  maintenance: "En maintenance",
  parking: "Hors service",
  archivé: "Archivé",
};

const operationalStateColors: Record<
  OperationalState,
  "success" | "primary" | "warning" | "info" | "default"
> = {
  disponible: "success",
  en_service: "primary",
  maintenance: "warning",
  parking: "info",
  archivé: "default",
};

const ownershipStatusLabels: Record<Vehicle["ownershipStatus"], string> = {
  leasing: "Leasing",
  location: "Location",
  propriétaire: "Propriétaire",
};

type VehicleType =
  | "vul"
  | "pl_fourgon"
  | "pl_citerne"
  | "pl_plateau"
  | "tracteur_routier"
  | "semi_fourgon"
  | "semi_citerne"
  | "semi_plateau"
  | "remorque_fourgon"
  | "remorque_citerne"
  | "remorque_plateau"
  | "vl_autocar"
  // Legacy types
  | "vl" | "pl" | "spl" | "bus" | "autocar" | "remorque"
  | "semi_remorque" | "fourgon" | "camion_benne" | "camion_citerne"
  | "porte_conteneur" | "frigorifique";

const vehicleTypeLabels: Record<string, string> = {
  vul: "Véhicule Utilitaire Léger",
  pl_fourgon: "Camion Poids Lourd Fourgon Rigide & Baché",
  pl_citerne: "Camion Poids Lourd Citerne",
  pl_plateau: "Camion Poids Lourd Plateau & Travaux publics",
  tracteur_routier: "Tracteur Routier",
  semi_fourgon: "Semi-Remorque Fourgon Rigide & Baché",
  semi_citerne: "Semi-Remorque Citerne",
  semi_plateau: "Semi-Remorque Plateau & Travaux publics",
  remorque_fourgon: "Remorque Fourgon Rigide & Baché",
  remorque_citerne: "Remorque Citerne",
  remorque_plateau: "Remorque Plateau & Travaux publics",
  vl_autocar: "Véhicule Léger & Autocar",
  // Legacy labels
  vl: "VL - Véhicule Léger",
  pl: "PL - Poids Lourd",
  spl: "SPL - Super Poids Lourd",
  bus: "Bus/Autocar",
  autocar: "Bus/Autocar",
  remorque: "Remorque",
  semi_remorque: "Semi-remorque",
  fourgon: "Fourgon",
  camion_benne: "Camion benne",
  camion_citerne: "Camion citerne",
  porte_conteneur: "Porte-conteneur",
  frigorifique: "Frigorifique",
};

const vehicleTypeOptions: VehicleType[] = [
  "vul",
  "pl_fourgon",
  "pl_citerne",
  "pl_plateau",
  "tracteur_routier",
  "semi_fourgon",
  "semi_citerne",
  "semi_plateau",
  "remorque_fourgon",
  "remorque_citerne",
  "remorque_plateau",
  "vl_autocar",
];

const fuelTypeLabels: Record<string, string> = {
  diesel: "Diesel",
  essence: "Essence",
  electrique: "Électrique",
  gnl: "Gaz Naturel Liquéfié",
  gnc: "Gaz Naturel Comprimé",
  gpl: "Gaz de Pétrole Liquéfiés",
  hybride_gnc: "Hybride Gaz Naturel Comprimé/Essence",
  hybride_essence: "Hybride Essence/Electrique",
  hybride_diesel: "Hybride Diesel/Electrique",
  biocarburant: "Bio carburant",
  sans_carburant: "Véhicule sans carburant",
  // Legacy
  gaz: "Gaz (GNV/GPL)",
  hybride: "Hybride",
};

const fuelTypeOptions: FuelType[] = [
  "diesel",
  "essence",
  "electrique",
  "gnl",
  "gnc",
  "gpl",
  "hybride_gnc",
  "hybride_essence",
  "hybride_diesel",
  "biocarburant",
  "sans_carburant",
];

// frigoTypeLabels utilisé dans le formulaire
const _frigoTypeLabels: Record<FrigoType, string> = {
  gasoil: "Gasoil",
  electrique: "Électrique",
};
void _frigoTypeLabels; // Éviter l'erreur unused

const ownershipStatusColors: Record<
  Vehicle["ownershipStatus"],
  "primary" | "info" | "success"
> = {
  leasing: "primary",
  location: "info",
  propriétaire: "success",
};

const archiveReasonOptions = [
  "Vendu",
  "Destruction",
  "Réforme",
  "Vol",
  "Accident total",
  "Fin de leasing",
  "Fin de location",
  "Trop ancien",
  "Trop de sinistres",
  "Autre",
];

const VehiclesPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsVehicle, setDetailsVehicle] = useState<Vehicle | null>(null);
  const [qrVehicle, setQrVehicle] = useState<Vehicle | null>(null);
  const [openArchive, setOpenArchive] = useState(false);
  const [archiveVehicle, setArchiveVehicle] = useState<Vehicle | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showArchived, setShowArchived] = useState(false);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBrand, setFilterBrand] = useState("");
  const [filterOperationalState, setFilterOperationalState] = useState("");
  const [filterOwnershipStatus, setFilterOwnershipStatus] = useState("");
  const [filterVehicleType, setFilterVehicleType] = useState("");
  const [sortBy, setSortBy] = useState<"plate" | "brand" | "model" | "year">(
    "plate"
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Archive reason
  const [archiveReason, setArchiveReason] = useState("");

  // Garage states
  const [garages, setGarages] = useState<Garage[]>([]);
  const [showGarageDialog, setShowGarageDialog] = useState(false);
  const [selectedGarage, setSelectedGarage] = useState<Garage | null>(null);
  const [garageForm, setGarageForm] = useState({
    maintenanceReason: "",
    estimatedExitDate: "",
  });
  const [pendingStateChange, setPendingStateChange] = useState<{
    vehicle: Vehicle;
    newState: OperationalState;
  } | null>(null);

  // Excel upload state
  const [uploadingExcel, setUploadingExcel] = useState(false);

  // Form state
  const [form, setForm] = useState<Vehicle>({
    plate: "",
    parkNumber: "",
    brand: "",
    model: "",
    year: "",
    mileage: "",
    vehicleType: "vl",
    fuelType: "diesel",
    ownershipStatus: "propriétaire",
    leasingEndDate: "",
    nextMaintenanceDate: "",
    technicalInspectionDate: "",
    technicalInspectionType: "controle_technique",
    operationalState: "disponible",
    firstRegistrationDate: "",
    acquisitionDate: "",
    registrationFees: undefined,
    monthlyPaymentAmount: undefined,
    firstPaymentDate: "",
    lastPaymentDate: "",
    annualInsuranceAmount: undefined,
    monthlyGeolocationAmount: undefined,
    wheelsCount: undefined,
    axlesCount: undefined,
    axleTaxAmount: undefined,
    hasChronotachygraphe: "sans",
    hasSpeedLimiter: "sans",
    hasFrigoGroup: "sans",
    extinguisherCount: undefined,
    frigoFuelType: "sans",
    frigoFuelTankCapacity: undefined,
    frigoConsumptionLH: undefined,
    frigoConsumptionL100: undefined,
    frigoAutonomyHours: undefined,
    frigoGroupInspectionDate: "",
    speedLimiterInspectionDate: "",
  });

  const initialPhotos = {
    driverSide: "",
    passengerSide: "",
    front: "",
    back: "",
    interior: "",
    engine: "",
    damage: "",
  };

  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState<{ [k: string]: string }>(initialPhotos);
  const [plaqueError, setPlaqueError] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Template sélectionné (pictogramme)
  const [selectedTemplate, setSelectedTemplate] =
    useState<VehicleTemplate | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryConfig | null>(null);

  // Fonction pour appliquer un template
  const applyTemplate = (template: VehicleTemplate) => {
    setSelectedTemplate(template);
    setForm((prev) => ({
      ...prev,
      vehicleType: template.vehicleType as VehicleType,
      fuelType: (template.fuelType as FuelType) || prev.fuelType,
      // Réservoirs
      fuelTankCapacity:
        template.defaultFuelTankCapacity || prev.fuelTankCapacity,
      adBlueTankCapacity:
        template.defaultAdBlueTankCapacity || prev.adBlueTankCapacity,
      adBlueRatio: template.defaultAdBlueRatio || prev.adBlueRatio,
      // Groupe frigo
      frigoType: template.frigoType || prev.frigoType,
      frigoTankCapacity: template.frigoTankCapacity || prev.frigoTankCapacity,
      frigoBatteryAutonomy:
        template.frigoBatteryAutonomy || prev.frigoBatteryAutonomy,
      // Dimensions & capacités utiles (pré-rempli depuis le template)
      payloadCapacity: template.payloadCapacity || prev.payloadCapacity,
      volumeCapacity: template.volumeCapacity || prev.volumeCapacity,
      innerLength: template.innerLength || prev.innerLength,
      innerWidth: template.innerWidth || prev.innerWidth,
      innerHeight: template.innerHeight || prev.innerHeight,
      compartmentsCount: template.compartmentsCount || prev.compartmentsCount,
      palletsEurope: template.palletsEurope || prev.palletsEurope,
      palletsISO: template.palletsISO || prev.palletsISO,
      // Équipements
      hasTailgate: template.hasHayon || prev.hasTailgate,
      tailgateCapacity: template.tailgateCapacity || prev.tailgateCapacity,
      hasCrane: template.hasGrue || prev.hasCrane,
      craneCapacity: template.craneCapacity || prev.craneCapacity,
    }));
    setTemplateDialogOpen(false);
    setSelectedCategory(null);
  };

  // Ouvrir le dialog de templates pour un nouveau véhicule
  const openTemplateDialog = () => {
    setTemplateDialogOpen(true);
    setSelectedCategory(null);
  };

  // Liste des marques et modèles (depuis l'API backend)
  const [carMakes, setCarMakes] = useState<string[]>([]);
  const [carModels, setCarModels] = useState<string[]>([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // Fetch vehicles
  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};

      // Si on veut voir les archivés, on filtre par état archivé
      // Sinon, on ne met pas de filtre (pour voir tous les non-archivés)
      if (showArchived) {
        params.operationalState = "archivé";
      }

      console.log("Fetching vehicles with params:", params);

      const { data } = await axios.get(`${API_URL}/vehicles`, { params });

      // Vérification de sécurité : s'assurer que data est un tableau
      const vehicleData = Array.isArray(data) ? data : (data?.data || data?.vehicles || []);
      if (!Array.isArray(vehicleData)) {
        console.error("La réponse de l'API n'est pas un tableau:", data);
        setVehicles([]);
        return;
      }
      const dataArray = vehicleData as Vehicle[];

      console.log(`Véhicules reçus: ${dataArray.length}`);
      console.log(
        "Données:",
        dataArray.map((v: Vehicle) => ({
          plate: v.plate,
          operationalState: v.operationalState,
        }))
      );

      // Filtrer les véhicules selon leur état opérationnel
      const filteredData = showArchived
        ? dataArray.filter((v: Vehicle) => v.operationalState === "archivé")
        : dataArray.filter((v: Vehicle) => v.operationalState !== "archivé");

      console.log(
        `Véhicules filtrés (showArchived=${showArchived}): ${filteredData.length}`
      );
      setVehicles(filteredData);
    } catch (error) {
      console.error("Erreur lors de la récupération des véhicules:", error);
      setVehicles([]); // Mettre un tableau vide en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [showArchived]);

  // Fetch garages
  useEffect(() => {
    const fetchGarages = async () => {
      try {
        const response = await axios.get(`${API_URL}/garages`);
        setGarages(response.data.garages || []);
      } catch (error) {
        console.error("Erreur lors du chargement des garages:", error);
        setGarages([]);
      }
    };
    fetchGarages();
  }, []);

  useEffect(() => {
    setLoadingMakes(true);
    axios
      .get(`${API_URL}/vehicle-models/brands`)
      .then((res) => {
        // Vérification de sécurité pour les marques
        const brands = Array.isArray(res.data) ? res.data : [];
        setCarMakes(brands);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des marques:", error);
        setCarMakes([]);
      })
      .finally(() => setLoadingMakes(false));
  }, []);

  useEffect(() => {
    if (form.brand) {
      setLoadingModels(true);
      axios
        .get(
          `${API_URL}/vehicle-models/brands/${encodeURIComponent(
            form.brand
          )}/models`
        )
        .then((res) => {
          // Vérification de sécurité pour les modèles
          const models = Array.isArray(res.data) ? res.data : [];
          setCarModels(models);
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération des modèles:", error);
          setCarModels([]);
        })
        .finally(() => setLoadingModels(false));
    } else {
      setCarModels([]);
    }
  }, [form.brand]);

  // Auto-calculate volume from dimensions
  useEffect(() => {
    const L = Number(form.innerLength);
    const W = Number(form.innerWidth);
    const H = Number(form.innerHeight);
    if (form.innerLength && form.innerWidth && form.innerHeight && L > 0 && W > 0 && H > 0) {
      const vol = Math.round(L * W * H * 100) / 100;
      setForm(prev => ({ ...prev, volumeCapacity: vol }));
    } else if (form.innerHeight === undefined || form.innerHeight === null || form.innerHeight === 0 || String(form.innerHeight) === '' || String(form.innerHeight) === '0') {
      // If height is missing, clear volume
      if (form.innerLength && form.innerWidth) {
        setForm(prev => ({ ...prev, volumeCapacity: undefined }));
      }
    }
  }, [form.innerLength, form.innerWidth, form.innerHeight]);

  // Auto-calculate palettes from dimensions
  useEffect(() => {
    const L = Number(form.innerLength);
    const W = Number(form.innerWidth);
    if (form.innerLength && form.innerWidth && L > 0 && W > 0) {
      // Palette Europe: 1.2m x 0.8m
      const euroA = Math.floor(L / 1.2) * Math.floor(W / 0.8);
      const euroB = Math.floor(L / 0.8) * Math.floor(W / 1.2);
      const palletsEurope = Math.max(euroA, euroB);
      // Palette ISO: 1.2m x 1.0m
      const isoA = Math.floor(L / 1.2) * Math.floor(W / 1.0);
      const isoB = Math.floor(L / 1.0) * Math.floor(W / 1.2);
      const palletsISO = Math.max(isoA, isoB);
      setForm(prev => ({ ...prev, palletsEurope, palletsISO }));
    }
  }, [form.innerLength, form.innerWidth]);

  // Auto-set fuelType for Semi-Remorque and Remorque
  useEffect(() => {
    if (form.vehicleType?.startsWith("semi_") || form.vehicleType?.startsWith("remorque_")) {
      setForm(prev => ({ ...prev, fuelType: "sans_carburant" as FuelType }));
    }
  }, [form.vehicleType]);

  // Handlers
  const handleOpenModal = (vehicle?: Vehicle) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setForm(vehicle);
      // Charger les photos existantes du véhicule
      if (vehicle.inspectionPhotos) {
        setPhotos({
          driverSide: vehicle.inspectionPhotos.driverSide || "",
          passengerSide: vehicle.inspectionPhotos.passengerSide || "",
          front: vehicle.inspectionPhotos.front || "",
          back: vehicle.inspectionPhotos.back || "",
          interior: vehicle.inspectionPhotos.interior || "",
          engine: vehicle.inspectionPhotos.engine || "",
          damage: vehicle.inspectionPhotos.damage || "",
        });
      } else {
        setPhotos(initialPhotos);
      }
      // Si le véhicule est en maintenance avec un garage, charger les infos
      if (vehicle.operationalState === "maintenance" && vehicle.currentGarage) {
        const garage = garages.find(
          (g) => g._id === vehicle.currentGarage?.garageId
        );
        if (garage) {
          setSelectedGarage(garage);
          setGarageForm({
            maintenanceReason: vehicle.currentGarage.maintenanceReason || "",
            estimatedExitDate: vehicle.currentGarage.estimatedExitDate || "",
          });
        }
      } else {
        setSelectedGarage(null);
        setGarageForm({ maintenanceReason: "", estimatedExitDate: "" });
      }
      setOpenModal(true);
    } else {
      setEditingVehicle(null);
      setSelectedTemplate(null);
      setPhotos(initialPhotos); // Réinitialiser les photos pour un nouveau véhicule
      setForm({
        plate: "",
        parkNumber: "",
        brand: "",
        model: "",
        year: "",
        mileage: "",
        vehicleType: "vl",
        fuelType: "diesel",
        ownershipStatus: "propriétaire",
        leasingEndDate: "",
        nextMaintenanceDate: "",
        technicalInspectionDate: "",
        technicalInspectionType: "controle_technique",
        operationalState: "disponible",
      });
      // Ouvrir d'abord le dialog de sélection de templates
      setTemplateDialogOpen(true);
      setSelectedCategory(null);
    }
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingVehicle(null);
    // Réinitialiser le sélecteur de templates
    setSelectedTemplate(null);
    setTemplateDialogOpen(false);
    // Réinitialiser les erreurs de validation
    setFormErrors({});
    // Réinitialiser le garage
    setSelectedGarage(null);
    setGarageForm({ maintenanceReason: "", estimatedExitDate: "" });
    setStep(0);
    setSelectedCategory(null);
    // Réinitialiser les photos
    setPhotos(initialPhotos);
  };
  const handleFormChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { name: string; value: string }
  ) => {
    if ("target" in e) {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      if (name === "plate") {
        // Format automatique XX-XXX-XX
        let raw = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
        if (raw.length > 7) raw = raw.slice(0, 7);
        let formatted = raw;
        if (raw.length > 2) formatted = raw.slice(0, 2) + "-" + raw.slice(2);
        if (raw.length > 5)
          formatted = formatted.slice(0, 6) + "-" + formatted.slice(6);
        // Validation
        const regex = /^[A-Z]{2}-\d{3}-[A-Z]{2}$/;
        if (formatted.length === 9 && !regex.test(formatted)) {
          setPlaqueError("Format attendu : XX-XXX-XX (ex : FR-123-AB)");
        } else {
          setPlaqueError("");
        }
        setForm((prev) => ({ ...prev, [name]: formatted }));
      } else {
        setForm((prev) => ({
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        }));
      }
    } else {
      // Cas Autocomplete : { name, value }
      setForm((prev) => ({ ...prev, [e.name]: e.value }));
    }
  };
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos((prev) => ({ ...prev, [name]: ev.target?.result as string }));
      };
      reader.readAsDataURL(files[0]);
    }
  };
  // Validation de l'étape 0 (informations de base)
  const validateStep0 = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.vehicleType) errors.vehicleType = "Catégorie de véhicule requise";
    if (!form.brand?.trim()) errors.brand = "Marque requise";
    if (!form.model?.trim()) errors.model = "Modèle requis";
    if (!form.plate?.trim()) errors.plate = "Immatriculation requise";
    if (!form.year) errors.year = "Année requise";
    if (
      form.mileage === undefined ||
      form.mileage === null ||
      form.mileage === ""
    )
      errors.mileage = "Kilométrage requis";
    if (!form.operationalState) errors.operationalState = "État requis";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    // Valider l'étape 0 avant de passer à l'étape 1
    if (step === 0) {
      if (!validateStep0()) {
        return; // Ne pas passer à l'étape suivante si validation échoue
      }
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setFormErrors({}); // Réinitialiser les erreurs quand on revient en arrière
    setStep((s) => s - 1);
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // On fusionne les photos dans le form
    const dataToSend = { ...form, inspectionPhotos: photos };
    console.log("Données envoyées au backend:", dataToSend);
    try {
      if (editingVehicle) {
        await axios.put(
          `${API_URL}/vehicles/${editingVehicle.plate}`,
          dataToSend
        );
      } else {
        await axios.post(`${API_URL}/vehicles`, dataToSend);
      }
      fetchVehicles();
      handleCloseModal();
      setStep(0);
      setPhotos(initialPhotos);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error("Erreur détaillée:", error.response?.data);
      }
    }
  };
  const handleOpenDetails = (vehicle: Vehicle) => {
    setDetailsVehicle(vehicle);
    setOpenDetails(true);
  };
  const handleCloseDetails = () => setOpenDetails(false);
  const handleOpenArchive = (vehicle: Vehicle) => {
    setArchiveVehicle(vehicle);
    setOpenArchive(true);
  };
  const handleCloseArchive = () => {
    setOpenArchive(false);
    setArchiveReason("");
  };
  const handleArchive = async () => {
    if (archiveVehicle) {
      try {
        await axios.patch(
          `${API_URL}/vehicles/${archiveVehicle.plate}/archive`,
          {
            archiveReason: archiveReason || undefined,
          }
        );
        fetchVehicles();
        handleCloseArchive();
      } catch (error) {
        console.error("Erreur lors de l'archivage:", error);
        if (error instanceof AxiosError) {
          alert(
            `Erreur lors de l'archivage: ${
              error.response?.data?.message || error.message
            }`
          );
        }
      }
    }
  };
  const handleUnarchive = async (vehicle: Vehicle) => {
    try {
      await axios.put(`${API_URL}/vehicles/${vehicle.plate}`, {
        ...vehicle,
        operationalState: "disponible",
      });
      fetchVehicles();
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(
          "Erreur lors de la désarchivation:",
          error.response?.data
        );
      }
    }
  };
  const handleOperationalStateChange = async (
    vehicle: Vehicle,
    newState: OperationalState
  ) => {
    // Bloquer le passage en "En service" si aucun chauffeur n'est assigné
    if (newState === "en_service" && !vehicle.hasAssignment) {
      alert("Impossible de passer en service : aucun chauffeur n'est assigné à ce véhicule.");
      return;
    }

    // Si on passe en maintenance ou parking, ouvrir le dialog de garage
    if (newState === "maintenance" || newState === "parking") {
      setPendingStateChange({ vehicle, newState });
      setSelectedGarage(null);
      setGarageForm({ maintenanceReason: "", estimatedExitDate: "" });
      setShowGarageDialog(true);
      return;
    }

    try {
      await axios.patch(
        `${API_URL}/vehicles/${vehicle.plate}/operational-state`,
        {
          operationalState: newState,
          currentGarage: undefined, // Nettoyer le garage quand on change d'état
        }
      );
      fetchVehicles();
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.message || "Erreur lors du changement de statut";
        alert(msg);
        console.error("Erreur lors du changement de statut:", error.response?.data);
      }
    }
  };

  // Fonction pour parser le fichier Excel et extraire les données de consommation
  const parseExcelFile = (file: File): Promise<Partial<Vehicle>> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });

          // Prendre la première feuille
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convertir en JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Extraire les données (chercher les valeurs dans différentes colonnes)
          const extractedData: Partial<Vehicle> = {};

          // Parcourir les lignes pour trouver les données
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i] as unknown[];
            if (!row || row.length === 0) continue;

            // Chercher les labels et valeurs
            const rowText = row
              .map((cell) => String(cell || ""))
              .join(" ")
              .toLowerCase();

            // Consommation constructeur
            if (
              rowText.includes("constructeur") ||
              rowText.includes("conso constructeur")
            ) {
              const numCell = row.find((cell) => typeof cell === "number") as
                | number
                | undefined;
              const value = parseFloat(String(numCell || row[1] || ""));
              if (!isNaN(value)) {
                if (detailsVehicle?.fuelType === "electrique") {
                  extractedData.consumptionElectricConstructor = value;
                } else if (detailsVehicle?.fuelType === "gaz") {
                  extractedData.consumptionGasConstructor = value;
                } else {
                  extractedData.consumptionFuelConstructor = value;
                }
              }
            }

            // Consommation réelle
            if (
              rowText.includes("réelle") ||
              rowText.includes("reelle") ||
              rowText.includes("conso réelle")
            ) {
              const numCell = row.find((cell) => typeof cell === "number") as
                | number
                | undefined;
              const value = parseFloat(String(numCell || row[1] || ""));
              if (!isNaN(value)) {
                if (detailsVehicle?.fuelType === "electrique") {
                  extractedData.consumptionElectricReal = value;
                } else if (detailsVehicle?.fuelType === "gaz") {
                  extractedData.consumptionGasReal = value;
                } else {
                  extractedData.consumptionFuelReal = value;
                }
              }
            }

            // Capacité réservoir
            if (
              rowText.includes("réservoir") ||
              rowText.includes("reservoir") ||
              rowText.includes("capacité")
            ) {
              const numCell = row.find((cell) => typeof cell === "number") as
                | number
                | undefined;
              const value = parseFloat(String(numCell || row[1] || ""));
              if (!isNaN(value)) {
                extractedData.fuelTankCapacity = value;
              }
            }

            // AdBlue
            if (
              rowText.includes("adblue") ||
              rowText.includes("ad blue") ||
              rowText.includes("ad-blue")
            ) {
              const numCell = row.find((cell) => typeof cell === "number") as
                | number
                | undefined;
              const value = parseFloat(String(numCell || row[1] || ""));
              if (!isNaN(value)) {
                extractedData.adBlueTankCapacity = value;
              }
            }

            // Ratio AdBlue
            if (rowText.includes("ratio") && rowText.includes("adblue")) {
              const numCell = row.find((cell) => typeof cell === "number") as
                | number
                | undefined;
              const value = parseFloat(String(numCell || row[1] || ""));
              if (!isNaN(value)) {
                extractedData.adBlueRatio = value;
              }
            }
          }

          // Si aucune donnée trouvée avec les labels, essayer de détecter automatiquement
          // en cherchant des nombres dans les premières colonnes
          if (Object.keys(extractedData).length === 0 && jsonData.length > 1) {
            // Prendre les premières valeurs numériques trouvées
            const firstRow = jsonData[0] as unknown[];
            const secondRow = jsonData[1] as unknown[];

            if (firstRow && secondRow) {
              // Chercher des nombres dans la deuxième ligne
              const numbers = secondRow.filter(
                (cell) => typeof cell === "number" && !isNaN(cell)
              ) as number[];

              if (numbers.length >= 2) {
                // Première valeur = constructeur, deuxième = réelle
                if (detailsVehicle?.fuelType === "electrique") {
                  extractedData.consumptionElectricConstructor =
                    numbers[0] as number;
                  extractedData.consumptionElectricReal = numbers[1] as number;
                } else if (detailsVehicle?.fuelType === "gaz") {
                  extractedData.consumptionGasConstructor =
                    numbers[0] as number;
                  extractedData.consumptionGasReal = numbers[1] as number;
                } else {
                  extractedData.consumptionFuelConstructor =
                    numbers[0] as number;
                  extractedData.consumptionFuelReal = numbers[1] as number;
                }
              }

              if (numbers.length >= 3) {
                extractedData.fuelTankCapacity = numbers[2] as number;
              }
            }
          }

          resolve(extractedData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  // Handler pour l'upload de fichier Excel
  const handleExcelUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !detailsVehicle) return;

    // Vérifier l'extension
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      alert("Veuillez sélectionner un fichier Excel (.xlsx ou .xls)");
      return;
    }

    setUploadingExcel(true);
    try {
      // Parser le fichier Excel
      const extractedData = await parseExcelFile(file);

      if (Object.keys(extractedData).length === 0) {
        alert("Aucune donnée de consommation trouvée dans le fichier Excel");
        setUploadingExcel(false);
        return;
      }

      // Mettre à jour le véhicule avec les données extraites
      const updatedVehicle = { ...detailsVehicle, ...extractedData };
      await axios.put(
        `${API_URL}/vehicles/${detailsVehicle.plate}`,
        updatedVehicle
      );

      // Rafraîchir les détails du véhicule
      const response = await axios.get(
        `${API_URL}/vehicles/${detailsVehicle.plate}`
      );
      setDetailsVehicle(response.data);

      // Rafraîchir la liste
      fetchVehicles();

      alert("Données de consommation importées avec succès !");
    } catch (error) {
      console.error("Erreur lors de l'import Excel:", error);
      if (error instanceof AxiosError) {
        alert(
          `Erreur lors de l'import: ${
            error.response?.data?.message || error.message
          }`
        );
      } else {
        alert("Erreur lors de la lecture du fichier Excel");
      }
    } finally {
      setUploadingExcel(false);
      // Réinitialiser l'input
      event.target.value = "";
    }
  };

  // Filtrage et tri
  const filteredAndSortedVehicles = vehicles
    .filter((v) => {
      // Recherche
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matches =
          v.plate.toLowerCase().includes(query) ||
          v.brand.toLowerCase().includes(query) ||
          v.model.toLowerCase().includes(query);
        if (!matches) return false;
      }

      // Filtre par marque
      if (filterBrand && v.brand !== filterBrand) return false;

      // Filtre par état opérationnel
      if (
        filterOperationalState &&
        v.operationalState !== filterOperationalState
      )
        return false;

      // Filtre par statut de propriété
      if (filterOwnershipStatus && v.ownershipStatus !== filterOwnershipStatus)
        return false;

      // Filtre par type de véhicule
      if (filterVehicleType && v.vehicleType !== filterVehicleType)
        return false;

      return true;
    })
    .sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (!aValue || !bValue) return 0;

      const comparison = aValue.toString().localeCompare(bValue.toString());
      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Pagination
  const paginatedVehicles = filteredAndSortedVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Statistiques calculées
  const stats = {
    total: vehicles.length,
    disponible: vehicles.filter((v) => v.operationalState === "disponible")
      .length,
    enService: vehicles.filter((v) => v.operationalState === "en_service")
      .length,
    maintenance: vehicles.filter((v) => v.operationalState === "maintenance")
      .length,
    horsService: vehicles.filter((v) => v.operationalState === "parking")
      .length,
  };

  // Formater le kilométrage avec des espaces
  const formatKm = (km: string | number) => {
    const num = typeof km === "string" ? parseInt(km) : km;
    return num.toLocaleString("fr-FR");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          background:
            "linear-gradient(145deg, #f0f4f8 0%, #e8eef5 50%, #f5f7fa 100%)",
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 2 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1400,
            mx: "auto",
          }}
        >
          {/* Header moderne */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                component={Link}
                to="/"
                startIcon={<ArrowBackIcon />}
                variant="text"
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                Retour
              </Button>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, sm: 1.5 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src="/vehicule-icon.png"
                    sx={{ width: { xs: 22, sm: 28 }, height: { xs: 22, sm: 28 }, filter: "brightness(0) invert(1)" }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      lineHeight: 1.2,
                      color: "#1a1a2e",
                      fontSize: { xs: "1.1rem", sm: "1.5rem" },
                    }}
                  >
                    Gestion des Véhicules
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {stats.total} véhicule{stats.total > 1 ? "s" : ""} dans le
                    parc
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Button
                component={Link}
                to="/historique-vehicules"
                startIcon={<HistoryIcon />}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{
                  borderRadius: 2,
                  borderColor: "#9c27b0",
                  color: "#9c27b0",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  "&:hover": {
                    borderColor: "#7b1fa2",
                    bgcolor: "rgba(156, 39, 176, 0.04)",
                  },
                }}
              >
                {isMobile ? "Hist." : "Historique"}
              </Button>
              <Button
                variant={showArchived ? "contained" : "outlined"}
                onClick={() => setShowArchived(!showArchived)}
                startIcon={<ArchiveIcon />}
                color={showArchived ? "warning" : "inherit"}
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  borderRadius: 2,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                {showArchived ? "Actifs" : "Archivés"}
              </Button>
              {!showArchived && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<CategoryIcon />}
                    onClick={() => navigate("/vehicle-template/create")}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      borderRadius: 2,
                      borderColor: "#00bcd4",
                      color: "#00bcd4",
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      display: { xs: "none", sm: "flex" },
                      "&:hover": {
                        borderColor: "#0097a7",
                        bgcolor: "rgba(0, 188, 212, 0.04)",
                      },
                    }}
                  >
                    Créer un template
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal()}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      background:
                        "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                      boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                      },
                    }}
                  >
                    {isMobile ? "Nouveau" : "Nouveau véhicule"}
                  </Button>
                  <Button
                    component={Link}
                    to="/vehicules/import"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      borderColor: "#4caf50",
                      color: "#4caf50",
                      "&:hover": {
                        borderColor: "#388e3c",
                        bgcolor: "rgba(76, 175, 80, 0.04)",
                      },
                    }}
                  >
                    {isMobile ? "Import" : "Import massif"}
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Statistiques */}
          {!showArchived && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                }}
              >
                <Typography variant="h3" fontWeight={800}>
                  {stats.total}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total véhicules
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(17, 153, 142, 0.4)",
                }}
              >
                <Typography variant="h3" fontWeight={800}>
                  {stats.disponible}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Disponibles
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(33, 150, 243, 0.4)",
                }}
              >
                <Typography variant="h3" fontWeight={800}>
                  {stats.enService}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  En service
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #FF512F 0%, #F09819 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 20px rgba(255, 81, 47, 0.4)",
                }}
              >
                <Typography variant="h3" fontWeight={800}>
                  {stats.maintenance + stats.horsService}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Maintenance / HS
                </Typography>
              </Box>
            </Box>
          )}

          {/* Contenu principal */}
          <Box
            sx={{
              boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
              borderRadius: 4,
              background: "#fff",
              p: { xs: 2, sm: 3 },
              minHeight: "60vh",
            }}
          >
            {/* Barre de recherche et filtres */}
            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                mb: 3,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Recherche */}
                <TextField
                  placeholder={isMobile ? "Rechercher..." : "Rechercher (immatriculation, marque, modèle...)"}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{ mr: 1, display: "flex", alignItems: "center" }}
                      >
                        🔍
                      </Box>
                    ),
                  }}
                />

                {/* Filtres */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                      lg: "repeat(6, 1fr)",
                    },
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <TextField
                    select
                    label="Marque"
                    value={filterBrand}
                    onChange={(e) => setFilterBrand(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Toutes</MenuItem>
                    {Array.from(new Set(vehicles.map((v) => v.brand).filter(Boolean))).sort().map(
                      (brand) => (
                        <MenuItem key={brand} value={brand}>
                          {brand}
                        </MenuItem>
                      )
                    )}
                  </TextField>

                  <TextField
                    select
                    label="État"
                    value={filterOperationalState}
                    onChange={(e) => setFilterOperationalState(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {Object.entries(operationalStateLabels).map(
                      ([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      )
                    )}
                  </TextField>

                  <TextField
                    select
                    label="Statut"
                    value={filterOwnershipStatus}
                    onChange={(e) => setFilterOwnershipStatus(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {Object.entries(ownershipStatusLabels).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Type"
                    value={filterVehicleType}
                    onChange={(e) => setFilterVehicleType(e.target.value)}
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="">Catégorie de véhicule</MenuItem>
                    {vehicleTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {vehicleTypeLabels[type]}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Tri */}
                  <TextField
                    select
                    label="Trier par"
                    value={sortBy}
                    onChange={(e) =>
                      setSortBy(
                        e.target.value as "plate" | "brand" | "model" | "year"
                      )
                    }
                    size="small"
                    fullWidth
                  >
                    <MenuItem value="plate">Immatriculation</MenuItem>
                    <MenuItem value="brand">Marque</MenuItem>
                    <MenuItem value="model">Modèle</MenuItem>
                    <MenuItem value="year">Année</MenuItem>
                  </TextField>

                  <Button
                    variant="outlined"
                    onClick={() =>
                      setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                    }
                    fullWidth
                    sx={{
                      minHeight: 40,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {sortOrder === "asc" ? "↑ A-Z" : "↓ Z-A"}
                  </Button>
                </Box>
              </Box>

              {/* Info résultats */}
              {(searchQuery ||
                filterBrand ||
                filterOperationalState ||
                filterOwnershipStatus ||
                filterVehicleType) && (
                <Box
                  sx={{ mt: 2, color: "text.secondary", fontSize: "0.875rem" }}
                >
                  {filteredAndSortedVehicles.length} résultat(s) sur{" "}
                  {vehicles.length} véhicule(s)
                </Box>
              )}
            </Box>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 300,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : isMobile ? (
              // Vue en cartes pour mobile
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {paginatedVehicles.map((v) => (
                  <Card
                    key={v.plate}
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: 4,
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      {/* Header avec plaque et état */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ fontSize: "1.1rem", mb: 0.5 }}
                          >
                            {v.plate}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {v.brand} {v.model}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5, flexDirection: "column", alignItems: "flex-end" }}>
                          <Chip
                            label={
                              v.vehicleType
                                ? vehicleTypeLabels[v.vehicleType]
                                : "VL"
                            }
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem" }}
                          />
                          <Chip
                            label={ownershipStatusLabels[v.ownershipStatus]}
                            color={ownershipStatusColors[v.ownershipStatus]}
                            size="small"
                            sx={{ fontSize: "0.7rem", mt: 0.5 }}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Informations principales */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Année
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {v.year || "—"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Kilométrage
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ fontFamily: "monospace" }}
                          >
                            {formatKm(v.mileage)} km
                          </Typography>
                        </Box>
                      </Box>

                      {/* État opérationnel */}
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.7rem", display: "block", mb: 0.5 }}
                        >
                          État opérationnel
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOperationalStateChange(v, "disponible")}
                            sx={{
                              bgcolor: v.operationalState === "disponible" ? "success.light" : "grey.100",
                              color: v.operationalState === "disponible" ? "success.dark" : "grey.500",
                              width: 32,
                              height: 32,
                              "&:hover": { bgcolor: "success.light" },
                            }}
                            title="Disponible"
                          >
                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOperationalStateChange(v, "maintenance")}
                            sx={{
                              bgcolor: v.operationalState === "maintenance" ? "warning.light" : "grey.100",
                              color: v.operationalState === "maintenance" ? "warning.dark" : "grey.500",
                              width: 32,
                              height: 32,
                              "&:hover": { bgcolor: "warning.light" },
                            }}
                            title="Maintenance"
                          >
                            <BuildIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <Chip
                            label={operationalStateLabels[v.operationalState]}
                            size="small"
                            sx={{
                              bgcolor: `${operationalStateColors[v.operationalState]}.lighter`,
                              color: `${operationalStateColors[v.operationalState]}.main`,
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              height: 32,
                            }}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Actions */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          <Tooltip title="Voir détails" arrow>
                            <IconButton
                              onClick={() => handleOpenDetails(v)}
                              size="small"
                              sx={{
                                bgcolor: "grey.100",
                                "&:hover": { bgcolor: "grey.200" },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="QR Code" arrow>
                            <IconButton
                              onClick={() => setQrVehicle(v)}
                              size="small"
                              sx={{
                                bgcolor: "purple.50",
                                color: "purple",
                                "&:hover": { bgcolor: "purple.100" },
                              }}
                            >
                              <QrCodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!showArchived && (
                            <>
                              <Tooltip title="Historique" arrow>
                                <IconButton
                                  component={Link}
                                  to={`/vehicules/historique/${v.plate}`}
                                  size="small"
                                  sx={{
                                    bgcolor: "secondary.50",
                                    color: "secondary.main",
                                    "&:hover": { bgcolor: "secondary.100" },
                                  }}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Modifier" arrow>
                                <IconButton
                                  onClick={() => handleOpenModal(v)}
                                  size="small"
                                  sx={{
                                    bgcolor: "primary.50",
                                    color: "primary.main",
                                    "&:hover": { bgcolor: "primary.100" },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {showArchived && (
                            <Tooltip title="Désarchiver" arrow>
                              <IconButton
                                onClick={() => handleUnarchive(v)}
                                size="small"
                                sx={{
                                  bgcolor: "primary.50",
                                  color: "primary.main",
                                  "&:hover": { bgcolor: "primary.100" },
                                }}
                              >
                                <UnarchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <TableContainer
                sx={{
                  borderRadius: 2,
                  overflow: "auto",
                  maxWidth: "100%",
                  "& .MuiTableCell-root": {
                    whiteSpace: "nowrap",
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                  "& .MuiTableCell-head": {
                    backgroundColor: "grey.100",
                    fontWeight: "bold",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                }}
              >
                <Table stickyHeader size={isTablet ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Immatriculation</TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", md: "table-cell" } }}
                      >
                        Type
                      </TableCell>
                      <TableCell>Marque</TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", sm: "table-cell" } }}
                      >
                        Modèle
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", lg: "table-cell" } }}
                      >
                        Année
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", sm: "table-cell" } }}
                      >
                        Km
                      </TableCell>
                      <TableCell
                        sx={{ display: { xs: "none", lg: "table-cell" } }}
                      >
                        Statut
                      </TableCell>
                      <TableCell>État</TableCell>
                      {showArchived && (
                        <TableCell
                          sx={{ display: { xs: "none", md: "table-cell" } }}
                        >
                          Motif
                        </TableCell>
                      )}
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedVehicles.length > 0 ? (
                      paginatedVehicles.map((v) => (
                        <TableRow key={v.plate} hover>
                          <TableCell>
                            <Box>
                              <Typography
                                fontWeight={600}
                                sx={{
                                  fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                }}
                              >
                                {v.plate}
                              </Typography>
                              {/* Afficher marque/modèle sous l'immat sur mobile */}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: { xs: "block", sm: "none" } }}
                              >
                                {v.brand} {v.model}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{ display: { xs: "none", md: "table-cell" } }}
                          >
                            <Chip
                              label={
                                v.vehicleType
                                  ? vehicleTypeLabels[v.vehicleType]
                                  : "VL"
                              }
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{v.brand}</TableCell>
                          <TableCell
                            sx={{ display: { xs: "none", sm: "table-cell" } }}
                          >
                            {v.model}
                          </TableCell>
                          <TableCell
                            sx={{ display: { xs: "none", lg: "table-cell" } }}
                          >
                            {v.year}
                          </TableCell>
                          <TableCell
                            sx={{
                              display: { xs: "none", sm: "table-cell" },
                              fontFamily: "monospace",
                              fontWeight: 500,
                            }}
                          >
                            {formatKm(v.mileage)} km
                          </TableCell>
                          <TableCell
                            sx={{ display: { xs: "none", lg: "table-cell" } }}
                          >
                            <Chip
                              label={ownershipStatusLabels[v.ownershipStatus]}
                              color={ownershipStatusColors[v.ownershipStatus]}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {/* Boutons rapides */}
                              <IconButton
                                size="small"
                                onClick={() => handleOperationalStateChange(v, "disponible")}
                                sx={{
                                  bgcolor: v.operationalState === "disponible" ? "success.light" : "grey.100",
                                  color: v.operationalState === "disponible" ? "success.dark" : "grey.500",
                                  width: 28,
                                  height: 28,
                                  "&:hover": { bgcolor: "success.light" },
                                }}
                                title="Disponible"
                              >
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleOperationalStateChange(v, "maintenance")}
                                sx={{
                                  bgcolor: v.operationalState === "maintenance" ? "warning.light" : "grey.100",
                                  color: v.operationalState === "maintenance" ? "warning.dark" : "grey.500",
                                  width: 28,
                                  height: 28,
                                  "&:hover": { bgcolor: "warning.light" },
                                }}
                                title="Maintenance"
                              >
                                <BuildIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <FormControl
                                size="small"
                                sx={{ minWidth: { xs: 80, sm: 100 }, display: { xs: "none", md: "flex" } }}
                              >
                              <Select
                                value={v.operationalState}
                                onChange={(e) =>
                                  handleOperationalStateChange(
                                    v,
                                    e.target.value as OperationalState
                                  )
                                }
                                size="small"
                                sx={{
                                  "& .MuiSelect-select": {
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    backgroundColor: `${
                                      operationalStateColors[v.operationalState]
                                    }.lighter`,
                                    color: `${
                                      operationalStateColors[v.operationalState]
                                    }.main`,
                                    fontWeight: 500,
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                  },
                                }}
                              >
                                {operationalStateOptions.map((state) => (
                                  <MenuItem
                                    key={state}
                                    value={state}
                                    sx={{
                                      color: `${
                                        operationalStateColors[
                                          state as OperationalState
                                        ]
                                      }.main`,
                                      "&.Mui-selected": {
                                        backgroundColor: `${
                                          operationalStateColors[
                                            state as OperationalState
                                          ]
                                        }.lighter`,
                                      },
                                      fontSize: {
                                        xs: "0.75rem",
                                        sm: "0.875rem",
                                      },
                                    }}
                                  >
                                    {
                                      operationalStateLabels[
                                        state as OperationalState
                                      ]
                                    }
                                  </MenuItem>
                                ))}
                                <MenuItem key="archivé" value="archivé">
                                  {operationalStateLabels.archivé}
                                </MenuItem>
                              </Select>
                            </FormControl>
                            </Box>
                          </TableCell>
                          {showArchived && (
                            <TableCell
                              sx={{ display: { xs: "none", md: "table-cell" } }}
                            >
                              <Chip
                                label={v.archiveReason || "Non spécifié"}
                                size="small"
                                color={v.archiveReason ? "warning" : "default"}
                                sx={{
                                  fontWeight: 500,
                                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Tooltip title="Voir détails" arrow>
                                <IconButton
                                  onClick={() => handleOpenDetails(v)}
                                  size="small"
                                  sx={{
                                    bgcolor: "grey.100",
                                    "&:hover": { bgcolor: "grey.200" },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="QR Code" arrow>
                                <IconButton
                                  onClick={() => setQrVehicle(v)}
                                  size="small"
                                  sx={{
                                    bgcolor: "purple.50",
                                    color: "purple",
                                    "&:hover": { bgcolor: "purple.100" },
                                  }}
                                >
                                  <QrCodeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {showArchived ? (
                                <Tooltip title="Désarchiver" arrow>
                                  <IconButton
                                    onClick={() => handleUnarchive(v)}
                                    size="small"
                                    sx={{
                                      bgcolor: "primary.50",
                                      color: "primary.main",
                                      "&:hover": { bgcolor: "primary.100" },
                                    }}
                                  >
                                    <UnarchiveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <>
                                  <Tooltip title="Historique" arrow>
                                    <IconButton
                                      component={Link}
                                      to={`/vehicules/historique/${v.plate}`}
                                      size="small"
                                      sx={{
                                        bgcolor: "secondary.50",
                                        color: "secondary.main",
                                        "&:hover": { bgcolor: "secondary.100" },
                                      }}
                                    >
                                      <HistoryIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Modifier" arrow>
                                    <IconButton
                                      onClick={() => handleOpenModal(v)}
                                      size="small"
                                      sx={{
                                        bgcolor: "info.50",
                                        color: "info.main",
                                        "&:hover": { bgcolor: "info.100" },
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Archiver" arrow>
                                    <IconButton
                                      onClick={() => handleOpenArchive(v)}
                                      size="small"
                                      sx={{
                                        bgcolor: "warning.50",
                                        color: "warning.main",
                                        "&:hover": { bgcolor: "warning.100" },
                                      }}
                                    >
                                      <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                          <Typography color="textSecondary">
                            Aucun véhicule trouvé.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              component="div"
              count={filteredAndSortedVehicles.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
              labelRowsPerPage={isMobile ? "Par page:" : "Véhicules par page:"}
              labelDisplayedRows={({ from, to, count }) =>
                isMobile
                  ? `${from}-${to} / ${count}`
                  : `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
              }
              sx={{
                mt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  gap: 1,
                  px: { xs: 1, sm: 2 },
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                },
                "& .MuiTablePagination-select": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                },
              }}
            />
          </Box>
        </Box>

        {/* Modale Création/Modification */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          disableEnforceFocus={false}
          disableAutoFocus={false}
          disableRestoreFocus={false}
        >
          <DialogTitle>
            {editingVehicle ? "Modifier le véhicule" : "Créer un véhicule"}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Stepper activeStep={step} alternativeLabel sx={{ mb: 2 }}>
                <Step>
                  <StepLabel>Informations</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Consommation</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Contrôles</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Photos</StepLabel>
                </Step>
              </Stepper>

              {/* Affichage du template sélectionné */}
              {!editingVehicle && step === 0 && selectedTemplate && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  icon={
                    <Box
                      component="img"
                      src={selectedTemplate.image}
                      alt={selectedTemplate.name}
                      sx={{ width: 40, height: 40, objectFit: "contain" }}
                    />
                  }
                  action={
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setTemplateDialogOpen(true)}
                        startIcon={<ImageSearchIcon />}
                      >
                        Changer
                      </Button>
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => navigate("/vehicle-library")}
                        startIcon={<SettingsIcon />}
                      >
                        Modifier template
                      </Button>
                    </Box>
                  }
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {selectedTemplate.name}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {selectedTemplate.description}
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                  >
                    {selectedTemplate.payloadCapacity && (
                      <Chip
                        label={`${selectedTemplate.payloadCapacity} kg`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {selectedTemplate.volumeCapacity && (
                      <Chip
                        label={`${selectedTemplate.volumeCapacity} m³`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {selectedTemplate.palletsEurope && (
                      <Chip
                        label={`${selectedTemplate.palletsEurope} pal.`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {selectedTemplate.controls.technicalInspection && (
                      <Chip label="CT VUL" size="small" color="info" />
                    )}
                    {selectedTemplate.controls.technicalInspectionPL && (
                      <Chip label="CT PL" size="small" color="info" />
                    )}
                    {selectedTemplate.controls.tachograph && (
                      <Chip label="Tachygraphe" size="small" color="primary" />
                    )}
                    {selectedTemplate.controls.tailgate && (
                      <Chip label="VGP Hayon" size="small" color="warning" />
                    )}
                    {selectedTemplate.controls.crane && (
                      <Chip label="VGP Grue" size="small" color="warning" />
                    )}
                    {selectedTemplate.controls.atp && (
                      <Chip label="ATP" size="small" color="success" />
                    )}
                    {selectedTemplate.controls.adr && (
                      <Chip label="ADR" size="small" color="error" />
                    )}
                  </Box>
                </Alert>
              )}

              {/* Bouton pour ouvrir le sélecteur si pas de template sélectionné */}
              {!editingVehicle && step === 0 && !selectedTemplate && (
                <Button
                  variant="contained"
                  startIcon={<ImageSearchIcon />}
                  onClick={() => setTemplateDialogOpen(true)}
                  sx={{ mb: 2, alignSelf: "flex-start" }}
                  color="primary"
                >
                  Sélectionner un type de véhicule
                </Button>
              )}

              {step === 0 && (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg, #f7fbff 0%, #f9f9f9 100%)",
                    p: { xs: 2, sm: 3 },
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box component="img" src="/vehicule-icon.png" sx={{ width: 24, height: 24, mr: 1 }} />
                    <Typography variant="h6" fontWeight={700}>
                      Informations principales
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    {/* Catégorie de véhicule EN PREMIER */}
                    <TextField
                      select
                      label="Catégorie de véhicule"
                      name="vehicleType"
                      value={form.vehicleType || "vl"}
                      onChange={(e) => {
                        handleFormChange(e);
                        // Réinitialiser marque et modèle quand on change le type
                        setForm((prev) => ({ ...prev, brand: "", model: "" }));
                        setFormErrors((prev) => ({ ...prev, vehicleType: "" }));
                      }}
                      required
                      fullWidth
                      size="small"
                      error={!!formErrors.vehicleType}
                      helperText={formErrors.vehicleType}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "primary.50",
                        },
                      }}
                    >
                      {vehicleTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {vehicleTypeLabels[type]}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Type de carburant */}
                    <TextField
                      select
                      label="Type de carburant"
                      name="fuelType"
                      value={form.fuelType || "diesel"}
                      onChange={handleFormChange}
                      required
                      fullWidth
                      size="small"
                      disabled={form.vehicleType?.startsWith("semi_") || form.vehicleType?.startsWith("remorque_")}
                    >
                      {fuelTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {fuelTypeLabels[type]}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Marque - filtrée selon le type de véhicule */}
                    <Autocomplete
                      options={getBrandsByVehicleType(form.vehicleType || "vl")}
                      value={form.brand || ""}
                      onChange={(_, value: string | null) => {
                        handleFormChange({
                          name: "brand",
                          value: value || "",
                        });
                        // Réinitialiser le modèle quand on change la marque
                        setForm((prev) => ({ ...prev, model: "" }));
                        setFormErrors((prev) => ({ ...prev, brand: "" }));
                      }}
                      freeSolo
                      clearOnEscape
                      noOptionsText="Aucune marque trouvée"
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Marque"
                          required
                          fullWidth
                          size="small"
                          error={!!formErrors.brand}
                          helperText={formErrors.brand}
                          placeholder={`Marques ${
                            vehicleTypeLabels[
                              form.vehicleType as VehicleType
                            ] || ""
                          }...`}
                        />
                      )}
                    />

                    {/* Modèle - filtré selon la marque */}
                    <Autocomplete
                      options={getModelsByBrand(
                        form.vehicleType || "vl",
                        form.brand || ""
                      )}
                      value={form.model || ""}
                      onChange={(_, value: string | null) => {
                        handleFormChange({
                          name: "model",
                          value: value || "",
                        });
                        setFormErrors((prev) => ({ ...prev, model: "" }));
                      }}
                      freeSolo
                      clearOnEscape
                      noOptionsText={
                        form.brand
                          ? "Saisissez le modèle"
                          : "Sélectionnez une marque d'abord"
                      }
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Modèle"
                          required
                          fullWidth
                          size="small"
                          disabled={!form.brand}
                          error={!!formErrors.model}
                          helperText={formErrors.model}
                          placeholder={
                            form.brand ? `Modèles ${form.brand}...` : ""
                          }
                        />
                      )}
                      disabled={!form.brand}
                    />

                    {/* Immatriculation */}
                    <TextField
                      label="Immatriculation"
                      name="plate"
                      value={form.plate}
                      onChange={(e) => {
                        handleFormChange(e);
                        setFormErrors((prev) => ({ ...prev, plate: "" }));
                      }}
                      required
                      fullWidth
                      size="small"
                      disabled={!!editingVehicle}
                      error={!!plaqueError || !!formErrors.plate}
                      helperText={
                        plaqueError || formErrors.plate || "Format: AA-123-BB"
                      }
                      inputProps={{
                        maxLength: 9,
                        style: {
                          textTransform: "uppercase",
                          letterSpacing: 2,
                          fontWeight: 600,
                        },
                      }}
                      sx={
                        plaqueError || formErrors.plate
                          ? { background: "#fff5f5" }
                          : {}
                      }
                    />

                    {/* Numéro du parc */}
                    <TextField
                      label="Numéro du parc"
                      name="parkNumber"
                      value={form.parkNumber || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      placeholder="Ex: PARC-001"
                    />

                    {/* Date de la première immatriculation */}
                    <TextField
                      label="Date de la première immatriculation"
                      name="firstRegistrationDate"
                      type="date"
                      value={form.firstRegistrationDate || ""}
                      onChange={(e) => {
                        handleFormChange(e);
                        // Auto-fill year from first registration date
                        if (e.target.value) {
                          const yearValue = e.target.value.slice(0, 4);
                          setForm((prev) => ({ ...prev, year: yearValue }));
                        }
                      }}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />

                    {/* Année */}
                    <TextField
                      label="Année"
                      name="year"
                      type="number"
                      value={form.year}
                      onChange={(e) => {
                        handleFormChange(e);
                        setFormErrors((prev) => ({ ...prev, year: "" }));
                      }}
                      required
                      fullWidth
                      size="small"
                      error={!!formErrors.year}
                      helperText={formErrors.year}
                      placeholder={new Date().getFullYear().toString()}
                      inputProps={{
                        min: 1990,
                        max: new Date().getFullYear() + 1,
                      }}
                    />

                    {/* Kilométrage */}
                    <TextField
                      label="Kilométrage"
                      name="mileage"
                      type="number"
                      value={form.mileage}
                      onChange={(e) => {
                        handleFormChange(e);
                        setFormErrors((prev) => ({ ...prev, mileage: "" }));
                      }}
                      required
                      fullWidth
                      size="small"
                      error={!!formErrors.mileage}
                      helperText={formErrors.mileage}
                      placeholder="0"
                      inputProps={{ min: 0 }}
                      InputProps={{
                        endAdornment: (
                          <Typography variant="caption" color="text.secondary">
                            km
                          </Typography>
                        ),
                      }}
                    />

                    {/* Champs financiers et immatriculation */}
                    <TextField
                      label="Date d'acquisition ou de location"
                      name="acquisitionDate"
                      type="date"
                      value={form.acquisitionDate || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Frais d'immatriculation (€)"
                      name="registrationFees"
                      type="number"
                      value={form.registrationFees || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                      label="Montant du paiement mensuel du véhicule (€)"
                      name="monthlyPaymentAmount"
                      type="number"
                      value={form.monthlyPaymentAmount || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                      label="Date du premier paiement mensuel"
                      name="firstPaymentDate"
                      type="date"
                      value={form.firstPaymentDate || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Date du dernier paiement mensuel"
                      name="lastPaymentDate"
                      type="date"
                      value={form.lastPaymentDate || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Montant de l'assurance annuel (€)"
                      name="annualInsuranceAmount"
                      type="number"
                      value={form.annualInsuranceAmount || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                      label="Montant mensuel de la géolocalisation (€)"
                      name="monthlyGeolocationAmount"
                      type="number"
                      value={form.monthlyGeolocationAmount || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    <TextField
                      select
                      label="Nombre de roues du véhicule"
                      name="wheelsCount"
                      value={form.wheelsCount || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">-</MenuItem>
                      {[2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Nombre d'essieux"
                      name="axlesCount"
                      value={form.axlesCount || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">-</MenuItem>
                      {[2, 3, 4, 5, 6, 7].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Montant de la taxe par essieu (€)"
                      name="axleTaxAmount"
                      type="number"
                      value={form.axleTaxAmount || ""}
                      onChange={handleFormChange}
                      fullWidth
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                    />

                    {/* État */}
                    <TextField
                      select
                      label="État"
                      name="operationalState"
                      value={form.operationalState}
                      onChange={(e) => {
                        const newState = e.target.value;
                        // Si on passe en maintenance ou parking, ouvrir le dialog de sélection de garage
                        if (
                          newState === "maintenance" ||
                          newState === "parking"
                        ) {
                          setShowGarageDialog(true);
                        } else if (
                          newState !== "maintenance" &&
                          newState !== "parking"
                        ) {
                          // Si on quitte l'état maintenance/parking, nettoyer le garage
                          setSelectedGarage(null);
                          setGarageForm({
                            maintenanceReason: "",
                            estimatedExitDate: "",
                          });
                          setForm((prev) => ({
                            ...prev,
                            currentGarage: undefined,
                          }));
                        }
                        handleFormChange(e);
                        setFormErrors((prev) => ({
                          ...prev,
                          operationalState: "",
                        }));
                      }}
                      required
                      fullWidth
                      size="small"
                      error={!!formErrors.operationalState}
                      helperText={formErrors.operationalState}
                    >
                      {operationalStateOptions.map((s) => (
                        <MenuItem key={s} value={s}>
                          <Chip
                            label={
                              operationalStateLabels[s as OperationalState]
                            }
                            size="small"
                            color={
                              operationalStateColors[s as OperationalState]
                            }
                            sx={{ mr: 1 }}
                          />
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Afficher le garage sélectionné si en maintenance ou parking */}
                    {(form.operationalState === "maintenance" ||
                      form.operationalState === "parking") &&
                      selectedGarage && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 2,
                            bgcolor:
                              form.operationalState === "parking"
                                ? "#E0F2FE"
                                : "#FEF3C7",
                            borderRadius: 2,
                            border:
                              form.operationalState === "parking"
                                ? "1px solid #7DD3FC"
                                : "1px solid #FCD34D",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                color={
                                  form.operationalState === "parking"
                                    ? "#0369A1"
                                    : "#92400E"
                                }
                              >
                                {form.operationalState === "parking"
                                  ? "🅿️ Stationné chez"
                                  : "🔧 Garage assigné"}
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {selectedGarage.nomCommercial}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {selectedGarage.adresse}, {selectedGarage.ville}
                              </Typography>
                              {garageForm.maintenanceReason && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    fontStyle: "italic",
                                  }}
                                >
                                  Motif: {garageForm.maintenanceReason}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              size="small"
                              onClick={() => setShowGarageDialog(true)}
                              sx={{ minWidth: "auto" }}
                            >
                              Modifier
                            </Button>
                          </Box>
                        </Box>
                      )}
                  </Box>
                </Box>
              )}
              {/* Étape 1: Consommation & Réservoirs */}
              {step === 1 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    maxHeight: "60vh",
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {/* Propriété */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      Propriété
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <TextField
                        select
                        label="Statut"
                        name="ownershipStatus"
                        value={form.ownershipStatus}
                        onChange={handleFormChange}
                        required
                        size="small"
                        sx={{ minWidth: 180 }}
                      >
                        <MenuItem value="propriétaire">Propriétaire</MenuItem>
                        <MenuItem value="leasing">Leasing</MenuItem>
                        <MenuItem value="location">Location</MenuItem>
                      </TextField>
                      {(form.ownershipStatus === "leasing" ||
                        form.ownershipStatus === "location") && (
                        <DatePicker
                          label={`Fin ${form.ownershipStatus}`}
                          value={
                            form.leasingEndDate
                              ? dayjs(form.leasingEndDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              leasingEndDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Dimensions & Capacités */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      📦 Dimensions & Capacités utiles
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <TextField
                        label="Charge utile (kg)"
                        name="payloadCapacity"
                        type="number"
                        value={form.payloadCapacity || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 140 }}
                      />
                      <TextField
                        label="Volume (m³)"
                        name="volumeCapacity"
                        type="number"
                        value={form.volumeCapacity || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 120 }}
                        InputProps={{
                          readOnly: !!(form.innerLength && form.innerWidth && form.innerHeight && Number(form.innerLength) > 0 && Number(form.innerWidth) > 0 && Number(form.innerHeight) > 0),
                        }}
                        helperText={form.innerLength && form.innerWidth && form.innerHeight && Number(form.innerLength) > 0 && Number(form.innerWidth) > 0 && Number(form.innerHeight) > 0 ? "Calculé automatiquement" : ""}
                      />
                      <TextField
                        label="Longueur intérieur (m)"
                        name="innerLength"
                        type="number"
                        value={form.innerLength || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 130 }}
                        inputProps={{ step: 0.01 }}
                      />
                      <TextField
                        label="Largeur intérieur (m)"
                        name="innerWidth"
                        type="number"
                        value={form.innerWidth || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 130 }}
                        inputProps={{ step: 0.01 }}
                      />
                      <TextField
                        label="Hauteur intérieur (m)"
                        name="innerHeight"
                        type="number"
                        value={form.innerHeight || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 130 }}
                        inputProps={{ step: 0.01 }}
                      />
                      <TextField
                        label="Compartiments"
                        name="compartmentsCount"
                        type="number"
                        value={form.compartmentsCount || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 120 }}
                      />
                      <TextField
                        label="Palette Europe"
                        name="palletsEurope"
                        type="number"
                        value={form.palletsEurope || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 110 }}
                        InputProps={{
                          readOnly: !!(form.innerLength && form.innerWidth && Number(form.innerLength) > 0 && Number(form.innerWidth) > 0),
                        }}
                        helperText={form.innerLength && form.innerWidth && Number(form.innerLength) > 0 && Number(form.innerWidth) > 0 ? "Calculé auto" : ""}
                      />
                      <TextField
                        label="Palette ISO"
                        name="palletsISO"
                        type="number"
                        value={form.palletsISO || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 100 }}
                        InputProps={{
                          readOnly: !!(form.innerLength && form.innerWidth && Number(form.innerLength) > 0 && Number(form.innerWidth) > 0),
                        }}
                        helperText={form.innerLength && form.innerWidth && Number(form.innerLength) > 0 && Number(form.innerWidth) > 0 ? "Calculé auto" : ""}
                      />
                    </Box>
                  </Box>

                  {/* Équipements (Hayon / Grue) */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      🔧 Équipements
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "center",
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={form.hasTailgate ? "oui" : "non"}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              hasTailgate: e.target.value === "oui",
                            }))
                          }
                          displayEmpty
                        >
                          <MenuItem value="non">Sans hayon</MenuItem>
                          <MenuItem value="oui">Avec hayon</MenuItem>
                        </Select>
                      </FormControl>
                      {form.hasTailgate && (
                        <TextField
                          label="Capacité hayon (kg)"
                          name="tailgateCapacity"
                          type="number"
                          value={form.tailgateCapacity || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 150 }}
                        />
                      )}
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={form.hasCrane ? "oui" : "non"}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              hasCrane: e.target.value === "oui",
                            }))
                          }
                          displayEmpty
                        >
                          <MenuItem value="non">Sans grue</MenuItem>
                          <MenuItem value="oui">Avec grue</MenuItem>
                        </Select>
                      </FormControl>
                      {form.hasCrane && (
                        <TextField
                          label="Capacité grue (kg)"
                          name="craneCapacity"
                          type="number"
                          value={form.craneCapacity || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 150 }}
                        />
                      )}
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={form.hasFrigoGroup || "sans"}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              hasFrigoGroup: e.target.value,
                            }))
                          }
                          displayEmpty
                        >
                          <MenuItem value="sans">Sans groupe frigorifique</MenuItem>
                          <MenuItem value="avec">Avec groupe frigorifique</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Extincteur (nombre)"
                        name="extinguisherCount"
                        type="number"
                        value={form.extinguisherCount || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 150 }}
                        inputProps={{ min: 0 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={form.hasChronotachygraphe || "sans"}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              hasChronotachygraphe: e.target.value,
                            }))
                          }
                          displayEmpty
                        >
                          <MenuItem value="sans">Sans chronotachygraphe</MenuItem>
                          <MenuItem value="avec">Avec chronotachygraphe</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={form.hasSpeedLimiter || "sans"}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              hasSpeedLimiter: e.target.value,
                            }))
                          }
                          displayEmpty
                        >
                          <MenuItem value="sans">Sans limiteur de vitesse</MenuItem>
                          <MenuItem value="avec">Avec limiteur de vitesse</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Consommation */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      Consommation{" "}
                      {form.fuelType === "electrique"
                        ? "(kWh/100km)"
                        : form.fuelType === "gaz"
                        ? "(kg/100km)"
                        : "(L/100km)"}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {form.fuelType === "electrique" ? (
                        <>
                          <TextField
                            label="Constructeur"
                            name="consumptionElectricConstructor"
                            type="number"
                            value={form.consumptionElectricConstructor || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                          <TextField
                            label="Réelle"
                            name="consumptionElectricReal"
                            type="number"
                            value={form.consumptionElectricReal || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                        </>
                      ) : form.fuelType === "gaz" ? (
                        <>
                          <TextField
                            label="Constructeur"
                            name="consumptionGasConstructor"
                            type="number"
                            value={form.consumptionGasConstructor || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                          <TextField
                            label="Réelle"
                            name="consumptionGasReal"
                            type="number"
                            value={form.consumptionGasReal || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                        </>
                      ) : (
                        <>
                          <TextField
                            label="Constructeur"
                            name="consumptionFuelConstructor"
                            type="number"
                            value={form.consumptionFuelConstructor || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                          <TextField
                            label="Réelle"
                            name="consumptionFuelReal"
                            type="number"
                            value={form.consumptionFuelReal || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Réservoirs */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      Réservoirs
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <TextField
                        label="Carburant (L)"
                        name="fuelTankCapacity"
                        type="number"
                        value={form.fuelTankCapacity || ""}
                        onChange={handleFormChange}
                        size="small"
                        sx={{ minWidth: 150 }}
                      />
                      {(form.fuelType === "diesel" ||
                        [
                          "pl",
                          "spl",
                          "camion_benne",
                          "camion_citerne",
                        ].includes(form.vehicleType || "")) && (
                        <>
                          <TextField
                            label="AdBlue (L)"
                            name="adBlueTankCapacity"
                            type="number"
                            value={form.adBlueTankCapacity || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                          <TextField
                            label="Conso AdBlue (L)"
                            name="adBlueConsumption"
                            type="number"
                            value={form.adBlueConsumption || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                          />
                          <TextField
                            label="Ratio AdBlue (%)"
                            name="adBlueRatio"
                            type="number"
                            value={form.adBlueRatio || ""}
                            onChange={handleFormChange}
                            size="small"
                            sx={{ minWidth: 150 }}
                            helperText="3-8%"
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Groupe frigorifique */}
                  {form.hasFrigoGroup === "avec" && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                      >
                        Groupe Frigorifique
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <TextField
                          select
                          label="Type"
                          name="frigoType"
                          value={form.frigoType || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 150 }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="gasoil">Gasoil</MenuItem>
                          <MenuItem value="electrique">Électrique</MenuItem>
                        </TextField>
                        {form.frigoType === "gasoil" && (
                          <>
                            <TextField
                              label="Conso constr. (L/h)"
                              name="frigoConsumptionGasoilConstructor"
                              type="number"
                              value={
                                form.frigoConsumptionGasoilConstructor || ""
                              }
                              onChange={handleFormChange}
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                            <TextField
                              label="Conso réelle (L/h)"
                              name="frigoConsumptionGasoilReal"
                              type="number"
                              value={form.frigoConsumptionGasoilReal || ""}
                              onChange={handleFormChange}
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                            <TextField
                              label="Réservoir frigo (L)"
                              name="frigoTankCapacity"
                              type="number"
                              value={form.frigoTankCapacity || ""}
                              onChange={handleFormChange}
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                          </>
                        )}
                        {form.frigoType === "electrique" && (
                          <>
                            <TextField
                              label="Conso constr. (kWh/h)"
                              name="frigoConsumptionElectricConstructor"
                              type="number"
                              value={
                                form.frigoConsumptionElectricConstructor || ""
                              }
                              onChange={handleFormChange}
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                            <TextField
                              label="Conso réelle (kWh/h)"
                              name="frigoConsumptionElectricReal"
                              type="number"
                              value={form.frigoConsumptionElectricReal || ""}
                              onChange={handleFormChange}
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                            <TextField
                              label="Autonomie (h)"
                              name="frigoBatteryAutonomy"
                              type="number"
                              value={form.frigoBatteryAutonomy || ""}
                              onChange={handleFormChange}
                              size="small"
                              sx={{ minWidth: 150 }}
                            />
                          </>
                        )}
                        <TextField
                          select
                          label="Type carburant complémentaire"
                          name="frigoFuelType"
                          value={form.frigoFuelType || "sans"}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 220 }}
                        >
                          <MenuItem value="sans">Sans carburant complémentaire</MenuItem>
                          <MenuItem value="diesel">Diesel</MenuItem>
                          <MenuItem value="electrique">Electrique</MenuItem>
                        </TextField>
                        <TextField
                          label="Capacité du réservoir (L)"
                          name="frigoFuelTankCapacity"
                          type="number"
                          value={form.frigoFuelTankCapacity || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 170 }}
                          inputProps={{ min: 0, step: 0.1 }}
                        />
                        <TextField
                          label="Consommation en (L/H)"
                          name="frigoConsumptionLH"
                          type="number"
                          value={form.frigoConsumptionLH || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 170 }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                        <TextField
                          label="Consommation en (L/100 km)"
                          name="frigoConsumptionL100"
                          type="number"
                          value={form.frigoConsumptionL100 || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 200 }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                        <TextField
                          label="Autonomie en (H)"
                          name="frigoAutonomyHours"
                          type="number"
                          value={form.frigoAutonomyHours || ""}
                          onChange={handleFormChange}
                          size="small"
                          sx={{ minWidth: 150 }}
                          inputProps={{ min: 0, step: 0.1 }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Étape 2: Contrôles obligatoires */}
              {step === 2 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    maxHeight: "60vh",
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {/* Contrôles généraux */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      Contrôles généraux
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <DatePicker
                        label="Prochain CT"
                        value={
                          form.technicalInspectionDate
                            ? dayjs(form.technicalInspectionDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            technicalInspectionDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                      <DatePicker
                        label="1er CT obligatoire"
                        value={
                          form.firstTechnicalInspectionDate
                            ? dayjs(form.firstTechnicalInspectionDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            firstTechnicalInspectionDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                      <DatePicker
                        label="Contrôle pollution"
                        value={
                          form.pollutionControlDate
                            ? dayjs(form.pollutionControlDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            pollutionControlDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                      <DatePicker
                        label="Prochaine maintenance"
                        value={
                          form.nextMaintenanceDate
                            ? dayjs(form.nextMaintenanceDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            nextMaintenanceDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Équipements */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                    >
                      Équipements
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <DatePicker
                        label="Hayon (semestriel)"
                        value={
                          form.tailgateInspectionDate
                            ? dayjs(form.tailgateInspectionDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            tailgateInspectionDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                      <DatePicker
                        label="Grue/Treuil"
                        value={
                          form.craneInspectionDate
                            ? dayjs(form.craneInspectionDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            craneInspectionDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                      <DatePicker
                        label="Extincteurs (annuel)"
                        value={
                          form.extinguisherInspectionDate
                            ? dayjs(form.extinguisherInspectionDate)
                            : null
                        }
                        onChange={(val) =>
                          setForm((f) => ({
                            ...f,
                            extinguisherInspectionDate: val
                              ? val.toISOString().slice(0, 10)
                              : "",
                          }))
                        }
                        format="YYYY-MM-DD"
                        slotProps={{
                          textField: { size: "small", sx: { minWidth: 180 } },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* PL */}
                  {[
                    "pl",
                    "spl",
                    "camion_benne",
                    "camion_citerne",
                    "porte_conteneur",
                  ].includes(form.vehicleType || "") && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                      >
                        Poids Lourds
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <DatePicker
                          label="CT PL (annuel)"
                          value={
                            form.technicalInspectionPLDate
                              ? dayjs(form.technicalInspectionPLDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              technicalInspectionPLDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        <DatePicker
                          label="Chronotachygraphe (2 ans)"
                          value={
                            form.tachographInspectionDate
                              ? dayjs(form.tachographInspectionDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              tachographInspectionDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        <DatePicker
                          label="Instruments pesage"
                          value={
                            form.weighingInstrumentsDate
                              ? dayjs(form.weighingInstrumentsDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              weighingInstrumentsDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        {(form.hasSpeedLimiter === "avec") && (
                          <DatePicker
                            label="Limiteur de vitesse (annuel)"
                            value={
                              form.speedLimiterInspectionDate
                                ? dayjs(form.speedLimiterInspectionDate)
                                : null
                            }
                            onChange={(val) =>
                              setForm((f) => ({
                                ...f,
                                speedLimiterInspectionDate: val
                                  ? val.toISOString().slice(0, 10)
                                  : "",
                              }))
                            }
                            format="YYYY-MM-DD"
                            slotProps={{
                              textField: { size: "small", sx: { minWidth: 220 } },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Limiteur de vitesse (hors PL) */}
                  {form.hasSpeedLimiter === "avec" && ![
                    "pl",
                    "spl",
                    "camion_benne",
                    "camion_citerne",
                    "porte_conteneur",
                  ].includes(form.vehicleType || "") && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                      >
                        Limiteur de vitesse
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <DatePicker
                          label="Limiteur de vitesse (annuel)"
                          value={
                            form.speedLimiterInspectionDate
                              ? dayjs(form.speedLimiterInspectionDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              speedLimiterInspectionDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 220 } },
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* ADR/Citerne */}
                  {form.vehicleType === "camion_citerne" && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                      >
                        ADR / Citerne
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <DatePicker
                          label="Certificat ADR"
                          value={form.adrDate ? dayjs(form.adrDate) : null}
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              adrDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        <DatePicker
                          label="Intermédiaire (2 ans)"
                          value={
                            form.adrIntermediateDate
                              ? dayjs(form.adrIntermediateDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              adrIntermediateDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        <DatePicker
                          label="Périodique (4 ans)"
                          value={
                            form.adrPeriodicDate
                              ? dayjs(form.adrPeriodicDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              adrPeriodicDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        <DatePicker
                          label="Flexibles (annuel)"
                          value={
                            form.adrHosesDate ? dayjs(form.adrHosesDate) : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              adrHosesDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Frigorifique */}
                  {(form.vehicleType === "frigorifique" || form.hasFrigoGroup === "avec") && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1, fontWeight: 600, color: "primary.main" }}
                      >
                        Frigorifique
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <DatePicker
                          label="Attestation ATP"
                          value={
                            form.atpCertificateDate
                              ? dayjs(form.atpCertificateDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              atpCertificateDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: { minWidth: 180 },
                              helperText: "6 ans, puis 3 ans",
                            },
                          }}
                        />
                        <DatePicker
                          label="Enregistreur temp."
                          value={
                            form.temperatureRecorderDate
                              ? dayjs(form.temperatureRecorderDate)
                              : null
                          }
                          onChange={(val) =>
                            setForm((f) => ({
                              ...f,
                              temperatureRecorderDate: val
                                ? val.toISOString().slice(0, 10)
                                : "",
                            }))
                          }
                          format="YYYY-MM-DD"
                          slotProps={{
                            textField: { size: "small", sx: { minWidth: 180 } },
                          }}
                        />
                        {form.hasFrigoGroup === "avec" && (
                          <DatePicker
                            label="Contrôle du groupe frigorifique (annuel)"
                            value={
                              form.frigoGroupInspectionDate
                                ? dayjs(form.frigoGroupInspectionDate)
                                : null
                            }
                            onChange={(val) =>
                              setForm((f) => ({
                                ...f,
                                frigoGroupInspectionDate: val
                                  ? val.toISOString().slice(0, 10)
                                  : "",
                              }))
                            }
                            format="YYYY-MM-DD"
                            slotProps={{
                              textField: { size: "small", sx: { minWidth: 280 } },
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Étape 3: Photos */}
              {step === 3 && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
                    <CameraIcon sx={{ color: "primary.main", mt: 0.3 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Prenez des photos de chaque face du véhicule. Ces photos serviront de référence pour les inspections extérieures.
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                        <LightbulbIcon sx={{ fontSize: 16, color: "info.main" }} />
                        <Typography variant="caption" color="info.main">
                          Si vous ne prenez pas de photos, les images par défaut seront utilisées lors des inspections.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                    {Object.entries(photos).map(([key, val]) => {
                      const photoLabels: Record<string, { label: string; icon: React.ReactNode; defaultImg: string }> = {
                        front: { label: "Avant", icon: <Box component="img" src="/vehicule-icon.png" sx={{ width: 18, height: 18 }} />, defaultImg: "/images/vehicles/face.png" },
                        back: { label: "Arrière", icon: <BackIcon sx={{ fontSize: 18 }} />, defaultImg: "/images/vehicles/derriere.png" },
                        driverSide: { label: "Côté conducteur", icon: <DoorIcon sx={{ fontSize: 18 }} />, defaultImg: "/images/vehicles/gauche.png" },
                        passengerSide: { label: "Côté passager", icon: <DoorIcon sx={{ fontSize: 18, transform: "scaleX(-1)" }} />, defaultImg: "/images/vehicles/droite.png" },
                        interior: { label: "Intérieur", icon: <SeatIcon sx={{ fontSize: 18 }} />, defaultImg: "/images/vehicles/interieur.png" },
                        engine: { label: "Moteur", icon: <EngineIcon sx={{ fontSize: 18 }} />, defaultImg: "/images/vehicles/moteur.png" },
                        damage: { label: "Sinistres existants", icon: <WarningIcon sx={{ fontSize: 18, color: "warning.main" }} />, defaultImg: "" },
                      };
                      const info = photoLabels[key] || { label: key, icon: <CameraIcon sx={{ fontSize: 18 }} />, defaultImg: "" };
                      
                      return (
                        <Box
                          key={key}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: 150,
                            p: 1.5,
                            borderRadius: 2,
                            border: val ? "2px solid #4caf50" : "2px dashed #ccc",
                            bgcolor: val ? "rgba(76, 175, 80, 0.05)" : "transparent",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              borderColor: "#1976d2",
                              bgcolor: "rgba(25, 118, 210, 0.05)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                            {info.icon}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {info.label}
                            </Typography>
                          </Box>
                          
                          {/* Prévisualisation */}
                          <Box
                            sx={{
                              width: 120,
                              height: 90,
                              borderRadius: 1,
                              overflow: "hidden",
                              bgcolor: "#f5f5f5",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            {val ? (
                              <img
                                src={val}
                                alt={info.label}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : info.defaultImg ? (
                              <img
                                src={info.defaultImg}
                                alt={`${info.label} par défaut`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  opacity: 0.4,
                                }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                Aucune photo
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Boutons */}
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Prendre une photo" arrow>
                              <Button
                                variant={val ? "outlined" : "contained"}
                                component="label"
                                size="small"
                                sx={{ minWidth: 0, px: 1 }}
                              >
                                <CameraIcon sx={{ fontSize: 18 }} />
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  hidden
                                  name={key}
                                  onChange={handlePhotoChange}
                                />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Choisir un fichier" arrow>
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                sx={{ minWidth: 0, px: 1 }}
                              >
                                <FolderIcon sx={{ fontSize: 18 }} />
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  name={key}
                                  onChange={handlePhotoChange}
                                />
                              </Button>
                            </Tooltip>
                            {val && (
                              <Tooltip title="Supprimer" arrow>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  sx={{ minWidth: 0, px: 1 }}
                                  onClick={() => setPhotos(prev => ({ ...prev, [key]: "" }))}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                          
                          {/* Statut */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                            {val && <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />}
                            <Typography variant="caption" color={val ? "success.main" : "text.secondary"}>
                              {val ? "Photo ajoutée" : "Image par défaut"}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {step > 0 && <Button onClick={handleBack}>Précédent</Button>}
              {step < 3 && (
                <Button
                  onClick={handleNext}
                  disabled={step === 0 && !!plaqueError}
                >
                  Suivant
                </Button>
              )}
              {step === 3 && (
                <Button type="submit" variant="contained">
                  {editingVehicle ? "Enregistrer" : "Créer"}
                </Button>
              )}
              <Button onClick={handleCloseModal}>Annuler</Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Modale Détails - Redesign complet */}
        <Dialog
          open={openDetails}
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
            },
          }}
        >
          {detailsVehicle && (
            <>
              {/* Header avec gradient */}
              <Box
                sx={{
                  background:
                    detailsVehicle.operationalState === "archivé"
                      ? "linear-gradient(135deg, #78716c 0%, #57534e 100%)"
                      : detailsVehicle.operationalState === "maintenance"
                      ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                      : detailsVehicle.operationalState === "en_service"
                      ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  p: 3,
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ opacity: 0.8, letterSpacing: 2 }}
                    >
                      {vehicleTypeLabels[detailsVehicle.vehicleType || "vl"]}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, letterSpacing: 1 }}
                    >
                      {detailsVehicle.plate}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, mt: 0.5 }}>
                      {detailsVehicle.brand} {detailsVehicle.model} •{" "}
                      {detailsVehicle.year}
                    </Typography>
                  </Box>
                  <Chip
                    label={
                      operationalStateLabels[detailsVehicle.operationalState]
                    }
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: 600,
                      backdropFilter: "blur(10px)",
                    }}
                  />
                </Box>
                {/* Stats rapides */}
                <Box sx={{ display: "flex", gap: 4, mt: 3 }}>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Kilométrage
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {Number(detailsVehicle.mileage || 0).toLocaleString()} km
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Carburant
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {fuelTypeLabels[detailsVehicle.fuelType || "diesel"]}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      Propriété
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {ownershipStatusLabels[detailsVehicle.ownershipStatus]}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Alerte Garage si en maintenance ou parking */}
              {(detailsVehicle.operationalState === "maintenance" ||
                detailsVehicle.operationalState === "parking") &&
                detailsVehicle.currentGarage && (
                  <Box
                    sx={{
                      mx: 3,
                      mt: -2,
                      mb: 2,
                      p: 2.5,
                      bgcolor:
                        detailsVehicle.operationalState === "parking"
                          ? "#E0F2FE"
                          : "#FFFBEB",
                      borderRadius: 2,
                      border:
                        detailsVehicle.operationalState === "parking"
                          ? "2px solid #7DD3FC"
                          : "2px solid #FCD34D",
                      boxShadow:
                        detailsVehicle.operationalState === "parking"
                          ? "0 4px 12px rgba(14, 165, 233, 0.2)"
                          : "0 4px 12px rgba(245, 158, 11, 0.2)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor:
                            detailsVehicle.operationalState === "parking"
                              ? "#0EA5E9"
                              : "#F59E0B",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "1.5rem",
                        }}
                      >
                        {detailsVehicle.operationalState === "parking"
                          ? "🅿️"
                          : "🔧"}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          color={
                            detailsVehicle.operationalState === "parking"
                              ? "#0369A1"
                              : "#92400E"
                          }
                        >
                          {detailsVehicle.operationalState === "parking"
                            ? "Stationné chez"
                            : "En maintenance chez"}{" "}
                          {detailsVehicle.currentGarage.garageName}
                        </Typography>
                        {detailsVehicle.currentGarage.garageAddress && (
                          <Typography variant="body2" color="text.secondary">
                            📍 {detailsVehicle.currentGarage.garageAddress}
                          </Typography>
                        )}
                        <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                          {detailsVehicle.currentGarage.maintenanceReason && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              <strong>Motif:</strong>{" "}
                              {detailsVehicle.currentGarage.maintenanceReason}
                            </Typography>
                          )}
                          {detailsVehicle.currentGarage.entryDate && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              <strong>Depuis le:</strong>{" "}
                              {dayjs(
                                detailsVehicle.currentGarage.entryDate
                              ).format("DD/MM/YYYY")}
                            </Typography>
                          )}
                          {detailsVehicle.currentGarage.estimatedExitDate && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              <strong>
                                {detailsVehicle.operationalState === "parking"
                                  ? "Récupération:"
                                  : "Sortie prévue:"}
                              </strong>{" "}
                              {dayjs(
                                detailsVehicle.currentGarage.estimatedExitDate
                              ).format("DD/MM/YYYY")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {detailsVehicle.currentGarage.garagePhone && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<span>📞</span>}
                          href={`tel:${detailsVehicle.currentGarage.garagePhone}`}
                          sx={{
                            borderColor:
                              detailsVehicle.operationalState === "parking"
                                ? "#0EA5E9"
                                : "#F59E0B",
                            color:
                              detailsVehicle.operationalState === "parking"
                                ? "#0369A1"
                                : "#92400E",
                          }}
                        >
                          Appeler
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

              <DialogContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 3,
                  }}
                >
                  {/* Carte Consommation */}
                  <Box
                    sx={{
                      bgcolor: "white",
                      borderRadius: 2,
                      p: 2.5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#3b82f6",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#1e293b" }}
                        >
                          Consommation
                        </Typography>
                      </Box>
                      <input
                        accept=".xlsx,.xls"
                        style={{ display: "none" }}
                        id="excel-upload-consumption"
                        type="file"
                        onChange={handleExcelUpload}
                        disabled={uploadingExcel}
                      />
                      <label htmlFor="excel-upload-consumption">
                        <Button
                          component="span"
                          variant="outlined"
                          size="small"
                          startIcon={<UploadIcon />}
                          disabled={uploadingExcel || !detailsVehicle}
                          sx={{ textTransform: "none" }}
                        >
                          {uploadingExcel ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              Import...
                            </>
                          ) : (
                            "Importer Excel"
                          )}
                        </Button>
                      </label>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, p: 1.5 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Constructeur
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#1e293b" }}
                        >
                          {detailsVehicle.fuelType === "electrique"
                            ? `${
                                detailsVehicle.consumptionElectricConstructor ||
                                "-"
                              } kWh/100km`
                            : detailsVehicle.fuelType === "gaz"
                            ? `${
                                detailsVehicle.consumptionGasConstructor || "-"
                              } kg/100km`
                            : `${
                                detailsVehicle.consumptionFuelConstructor || "-"
                              } L/100km`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, p: 1.5 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Réelle
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#1e293b" }}
                        >
                          {detailsVehicle.fuelType === "electrique"
                            ? `${
                                detailsVehicle.consumptionElectricReal || "-"
                              } kWh/100km`
                            : detailsVehicle.fuelType === "gaz"
                            ? `${
                                detailsVehicle.consumptionGasReal || "-"
                              } kg/100km`
                            : `${
                                detailsVehicle.consumptionFuelReal || "-"
                              } L/100km`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, p: 1.5 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Réservoir
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#1e293b" }}
                        >
                          {detailsVehicle.fuelTankCapacity || "-"} L
                        </Typography>
                      </Box>
                      {detailsVehicle.adBlueTankCapacity && (
                        <Box
                          sx={{ bgcolor: "#dbeafe", borderRadius: 1.5, p: 1.5 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            AdBlue
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, color: "#1d4ed8" }}
                          >
                            {detailsVehicle.adBlueTankCapacity} L (
                            {detailsVehicle.adBlueRatio || "-"}%)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Carte Contrôles */}
                  <Box
                    sx={{
                      bgcolor: "white",
                      borderRadius: 2,
                      p: 2.5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#f59e0b",
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "#1e293b" }}
                      >
                        Contrôles obligatoires
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {[
                        {
                          label: "Contrôle technique",
                          value: detailsVehicle.technicalInspectionDate,
                        },
                        {
                          label: "Contrôle pollution",
                          value: detailsVehicle.pollutionControlDate,
                        },
                        {
                          label: "Prochaine maintenance",
                          value: detailsVehicle.nextMaintenanceDate,
                        },
                        {
                          label: "Extincteurs",
                          value: detailsVehicle.extinguisherInspectionDate,
                        },
                      ]
                        .filter((item) => item.value)
                        .map((item, idx) => {
                          const isExpired =
                            item.value && new Date(item.value) < new Date();
                          const isSoon =
                            item.value &&
                            !isExpired &&
                            new Date(item.value) <
                              new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                          return (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: 1,
                                px: 1.5,
                                borderRadius: 1,
                                bgcolor: isExpired
                                  ? "#fef2f2"
                                  : isSoon
                                  ? "#fffbeb"
                                  : "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.label}
                              </Typography>
                              <Chip
                                size="small"
                                label={item.value || "-"}
                                sx={{
                                  bgcolor: isExpired
                                    ? "#ef4444"
                                    : isSoon
                                    ? "#f59e0b"
                                    : "#e2e8f0",
                                  color:
                                    isExpired || isSoon ? "white" : "#64748b",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              />
                            </Box>
                          );
                        })}
                    </Box>
                  </Box>

                  {/* Carte Frigo (si applicable) */}
                  {detailsVehicle.vehicleType === "frigorifique" &&
                    detailsVehicle.frigoType && (
                      <Box
                        sx={{
                          bgcolor: "white",
                          borderRadius: 2,
                          p: 2.5,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          border: "1px solid #e2e8f0",
                          background:
                            "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "#06b6d4",
                            }}
                          />
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: "#0e7490" }}
                          >
                            Groupe Frigorifique •{" "}
                            {detailsVehicle.frigoType === "gasoil"
                              ? "Gasoil"
                              : "Électrique"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: "rgba(255,255,255,0.7)",
                              borderRadius: 1.5,
                              p: 1.5,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Constructeur
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: "#0e7490" }}
                            >
                              {detailsVehicle.frigoType === "gasoil"
                                ? `${
                                    detailsVehicle.frigoConsumptionGasoilConstructor ||
                                    "-"
                                  } L/h`
                                : `${
                                    detailsVehicle.frigoConsumptionElectricConstructor ||
                                    "-"
                                  } kWh/h`}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              bgcolor: "rgba(255,255,255,0.7)",
                              borderRadius: 1.5,
                              p: 1.5,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Réelle
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: "#0e7490" }}
                            >
                              {detailsVehicle.frigoType === "gasoil"
                                ? `${
                                    detailsVehicle.frigoConsumptionGasoilReal ||
                                    "-"
                                  } L/h`
                                : `${
                                    detailsVehicle.frigoConsumptionElectricReal ||
                                    "-"
                                  } kWh/h`}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              bgcolor: "rgba(255,255,255,0.7)",
                              borderRadius: 1.5,
                              p: 1.5,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {detailsVehicle.frigoType === "gasoil"
                                ? "Réservoir"
                                : "Autonomie"}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: "#0e7490" }}
                            >
                              {detailsVehicle.frigoType === "gasoil"
                                ? `${detailsVehicle.frigoTankCapacity || "-"} L`
                                : `${
                                    detailsVehicle.frigoBatteryAutonomy || "-"
                                  } h`}
                            </Typography>
                          </Box>
                        </Box>
                        {(detailsVehicle.atpCertificateDate ||
                          detailsVehicle.temperatureRecorderDate) && (
                          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                            {detailsVehicle.atpCertificateDate && (
                              <Chip
                                size="small"
                                label={`ATP: ${detailsVehicle.atpCertificateDate}`}
                                sx={{ bgcolor: "rgba(255,255,255,0.8)" }}
                              />
                            )}
                            {detailsVehicle.temperatureRecorderDate && (
                              <Chip
                                size="small"
                                label={`Enreg. temp: ${detailsVehicle.temperatureRecorderDate}`}
                                sx={{ bgcolor: "rgba(255,255,255,0.8)" }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                  {/* Carte Poids Lourd (si applicable) */}
                  {[
                    "pl",
                    "spl",
                    "camion_benne",
                    "camion_citerne",
                    "porte_conteneur",
                    "bus",
                    "autocar",
                  ].includes(detailsVehicle.vehicleType || "") && (
                    <Box
                      sx={{
                        bgcolor: "white",
                        borderRadius: 2,
                        p: 2.5,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        border: "1px solid #e2e8f0",
                        background:
                          "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#d97706",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#92400e" }}
                        >
                          Contrôles Poids Lourd
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {detailsVehicle.technicalInspectionPLDate && (
                          <Chip
                            label={`CT PL: ${detailsVehicle.technicalInspectionPLDate}`}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.8)",
                              fontWeight: 500,
                            }}
                          />
                        )}
                        {detailsVehicle.tachographInspectionDate && (
                          <Chip
                            label={`Chrono: ${detailsVehicle.tachographInspectionDate}`}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.8)",
                              fontWeight: 500,
                            }}
                          />
                        )}
                        {detailsVehicle.tailgateInspectionDate && (
                          <Chip
                            label={`Hayon: ${detailsVehicle.tailgateInspectionDate}`}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.8)",
                              fontWeight: 500,
                            }}
                          />
                        )}
                        {detailsVehicle.craneInspectionDate && (
                          <Chip
                            label={`Grue: ${detailsVehicle.craneInspectionDate}`}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.8)",
                              fontWeight: 500,
                            }}
                          />
                        )}
                        {detailsVehicle.weighingInstrumentsDate && (
                          <Chip
                            label={`Pesage: ${detailsVehicle.weighingInstrumentsDate}`}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.8)",
                              fontWeight: 500,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Carte ADR (si applicable) */}
                  {detailsVehicle.vehicleType === "camion_citerne" && (
                    <Box
                      sx={{
                        bgcolor: "white",
                        borderRadius: 2,
                        p: 2.5,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        border: "1px solid #e2e8f0",
                        background:
                          "linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#db2777",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#9d174d" }}
                        >
                          Certifications ADR / Citerne
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.5,
                        }}
                      >
                        {[
                          {
                            label: "Certificat ADR",
                            value: detailsVehicle.adrDate,
                          },
                          {
                            label: "Intermédiaire",
                            value: detailsVehicle.adrIntermediateDate,
                          },
                          {
                            label: "Périodique",
                            value: detailsVehicle.adrPeriodicDate,
                          },
                          {
                            label: "Flexibles",
                            value: detailsVehicle.adrHosesDate,
                          },
                        ].map((item, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              bgcolor: "rgba(255,255,255,0.7)",
                              borderRadius: 1,
                              p: 1.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#9d174d" }}
                            >
                              {item.value || "-"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Carte Leasing (si applicable) */}
                  {(detailsVehicle.ownershipStatus === "leasing" ||
                    detailsVehicle.ownershipStatus === "location") &&
                    detailsVehicle.leasingEndDate && (
                      <Box
                        sx={{
                          bgcolor: "white",
                          borderRadius: 2,
                          p: 2.5,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          border: "1px solid #e2e8f0",
                          background:
                            "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "#7c3aed",
                            }}
                          />
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: "#5b21b6" }}
                          >
                            {detailsVehicle.ownershipStatus === "leasing"
                              ? "Leasing"
                              : "Location"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            bgcolor: "rgba(255,255,255,0.7)",
                            borderRadius: 1.5,
                            p: 2,
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Fin du contrat
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color: "#5b21b6" }}
                          >
                            {detailsVehicle.leasingEndDate}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                  {/* Carte Sinistres & Coûts */}
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      bgcolor: "white",
                      borderRadius: 2,
                      p: 2.5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#ef4444",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#1e293b" }}
                        >
                          Sinistres & Coûts
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(`/sinistres/${detailsVehicle.plate}`)
                        }
                        sx={{ borderRadius: 2, textTransform: "none" }}
                      >
                        Voir détails
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr 1fr",
                          md: "repeat(4, 1fr)",
                        },
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: "#fef2f2",
                          borderRadius: 1.5,
                          p: 1.5,
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { transform: "scale(1.02)", boxShadow: 1 },
                        }}
                        onClick={() =>
                          navigate(`/sinistres/${detailsVehicle.plate}`)
                        }
                      >
                        <Typography variant="caption" color="text.secondary">
                          Sinistres
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: "#dc2626" }}
                        >
                          -
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "#fef3c7",
                          borderRadius: 1.5,
                          p: 1.5,
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { transform: "scale(1.02)", boxShadow: 1 },
                        }}
                        onClick={() => navigate(`/garages`)}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Réparations
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: "#d97706" }}
                        >
                          -
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "#dbeafe",
                          borderRadius: 1.5,
                          p: 1.5,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Coût total
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: "#1d4ed8" }}
                        >
                          - €
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "#f0fdf4",
                          borderRadius: 1.5,
                          p: 1.5,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Dernier sinistre
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#16a34a" }}
                        >
                          Aucun
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Carte Archivage (si applicable) */}
                  {detailsVehicle.archiveReason && (
                    <Box
                      sx={{
                        gridColumn: "1 / -1",
                        bgcolor: "#fef2f2",
                        borderRadius: 2,
                        p: 2.5,
                        border: "1px solid #fecaca",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#ef4444",
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#dc2626" }}
                        >
                          Véhicule archivé
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ color: "#991b1b" }}>
                        Motif : <b>{detailsVehicle.archiveReason}</b>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions
                sx={{ p: 2, bgcolor: "white", borderTop: "1px solid #e2e8f0" }}
              >
                <Button
                  onClick={() =>
                    navigate(`/vehicules/historique/${detailsVehicle.plate}`)
                  }
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  Voir l'historique
                </Button>
                <Button
                  onClick={handleCloseDetails}
                  variant="contained"
                  sx={{ borderRadius: 2 }}
                >
                  Fermer
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Modale Archivage */}
        <Dialog
          open={openArchive}
          onClose={handleCloseArchive}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus={false}
          disableAutoFocus={false}
          disableRestoreFocus={false}
        >
          <DialogTitle>Archiver le véhicule</DialogTitle>
          <DialogContent dividers>
            <Typography>
              Voulez-vous vraiment archiver ce véhicule&nbsp;?
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              Plaque&nbsp;: <b>{archiveVehicle?.plate}</b>
            </Typography>
            <TextField
              select
              label="Motif d'archivage"
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              fullWidth
              size="small"
              helperText="Sélectionnez la raison de l'archivage"
              sx={{ mt: 2 }}
            >
              {archiveReasonOptions.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseArchive}>Annuler</Button>
            <Button onClick={handleArchive} color="warning" variant="contained">
              Archiver
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de sélection des pictogrammes par catégorie */}
        <Dialog
          open={templateDialogOpen}
          onClose={() => {
            if (selectedTemplate) {
              setTemplateDialogOpen(false);
              setSelectedCategory(null);
              setOpenModal(true);
            }
          }}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 3,
              maxHeight: isMobile ? "100vh" : "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              color: "white",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1, sm: 2 },
              p: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <ImageSearchIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                  {selectedCategory
                    ? selectedCategory.name
                    : "Sélectionnez une catégorie"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: "0.75rem", sm: "0.875rem" }, display: { xs: "none", sm: "block" } }}>
                  {selectedCategory
                    ? "Cliquez sur un véhicule pour le sélectionner"
                    : "Cliquez sur une catégorie pour voir les véhicules disponibles"}
                </Typography>
              </Box>
              {/* Bouton fermer sur mobile */}
              {isMobile && (
                <IconButton 
                  onClick={() => {
                    setTemplateDialogOpen(false);
                    setSelectedCategory(null);
                  }}
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setTemplateDialogOpen(false);
                  navigate("/vehicle-template/create");
                }}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.5)",
                  flex: { xs: 1, sm: "none" },
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                  py: { xs: 0.5, sm: 1 },
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
                startIcon={<AddIcon />}
              >
                Créer
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setTemplateDialogOpen(false);
                  navigate("/vehicle-library");
                }}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.5)",
                  flex: { xs: 1, sm: "none" },
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                  py: { xs: 0.5, sm: 1 },
                  "&:hover": {
                    borderColor: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
                startIcon={<SettingsIcon />}
              >
                Gérer
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {/* Vue des catégories */}
            {!selectedCategory && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                    lg: "repeat(5, 1fr)",
                  },
                  gap: { xs: 1.5, sm: 2 },
                  p: { xs: 2, sm: 3 },
                }}
              >
                {categoryConfigs.map((category) => (
                  <Box
                    key={category.name}
                    onClick={() => setSelectedCategory(category)}
                    sx={{
                      border: "2px solid",
                      borderColor: "grey.200",
                      borderRadius: { xs: 2, sm: 3 },
                      p: { xs: 1.5, sm: 2 },
                      cursor: "pointer",
                      transition: "all 0.3s",
                      bgcolor: "white",
                      "&:hover": {
                        borderColor: category.color,
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 24px ${category.color}30`,
                      },
                      "&:active": {
                        transform: "scale(0.98)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: { xs: 50, sm: 80 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <Box
                        component="img"
                        src={category.representativeImage}
                        alt={category.name}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      textAlign="center"
                      sx={{ color: category.color, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {category.shortName}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      textAlign="center"
                      color="text.secondary"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      {category.description}
                    </Typography>
                    <Chip
                      label={`${
                        vehicleTemplates.filter(
                          (t) => t.category === category.name
                        ).length
                      } véhicules`}
                      size="small"
                      sx={{
                        mt: { xs: 0.5, sm: 1 },
                        width: "100%",
                        bgcolor: `${category.color}15`,
                        color: category.color,
                        fontWeight: 600,
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        height: { xs: 20, sm: 24 },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Vue des véhicules d'une catégorie */}
            {selectedCategory && (
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setSelectedCategory(null)}
                  sx={{ mb: 2, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  size={isMobile ? "small" : "medium"}
                >
                  Retour aux catégories
                </Button>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {vehicleTemplates
                    .filter((t) => t.category === selectedCategory.name)
                    .map((template) => (
                      <Tooltip
                        key={template.id}
                        title={
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {template.name}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {template.description}
                            </Typography>
                            {template.equipments &&
                              template.equipments.length > 0 && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  <strong>Équipements:</strong>{" "}
                                  {template.equipments.join(", ")}
                                </Typography>
                              )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          onClick={() => {
                            applyTemplate(template);
                            setOpenModal(true);
                          }}
                          sx={{
                            border: "2px solid",
                            borderColor: "grey.200",
                            borderRadius: { xs: 2, sm: 3 },
                            p: { xs: 1.5, sm: 2 },
                            cursor: "pointer",
                            transition: "all 0.2s",
                            bgcolor: "white",
                            "&:hover": {
                              borderColor: selectedCategory.color,
                              transform: "scale(1.03)",
                              boxShadow: 3,
                            },
                            "&:active": {
                              transform: "scale(0.98)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              height: { xs: 60, sm: 100 },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: { xs: 0.5, sm: 1 },
                            }}
                          >
                            <Box
                              component="img"
                              src={template.image}
                              alt={template.name}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                          </Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            textAlign="center"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            {template.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            textAlign="center"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: { xs: "none", sm: "-webkit-box" },
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              minHeight: 32,
                            }}
                          >
                            {template.description}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions 
            sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              borderTop: 1, 
              borderColor: "divider",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
            }}
          >
            <Button
              onClick={() => {
                setTemplateDialogOpen(false);
                setSelectedCategory(null);
              }}
              sx={{ order: { xs: 2, sm: 1 } }}
              fullWidth={isMobile}
            >
              Annuler
            </Button>
            {!selectedCategory && (
              <Button
                variant="outlined"
                onClick={() => {
                  setTemplateDialogOpen(false);
                  setSelectedCategory(null);
                  setOpenModal(true);
                }}
                sx={{ order: { xs: 1, sm: 2 }, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                fullWidth={isMobile}
              >
                Continuer sans pictogramme
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Dialog sélection de garage */}
        <Dialog
          open={showGarageDialog}
          onClose={() => setShowGarageDialog(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
          <DialogTitle
            sx={{
              bgcolor:
                pendingStateChange?.newState === "parking" ||
                (!pendingStateChange && form.operationalState === "parking")
                  ? "#E0F2FE"
                  : "#FEF3C7",
              borderBottom:
                pendingStateChange?.newState === "parking" ||
                (!pendingStateChange && form.operationalState === "parking")
                  ? "1px solid #7DD3FC"
                  : "1px solid #FCD34D",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              justifyContent: "space-between"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor:
                  pendingStateChange?.newState === "parking" ||
                  (!pendingStateChange && form.operationalState === "parking")
                    ? "#0EA5E9"
                    : "#F59E0B",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "1.2rem",
              }}
            >
              {pendingStateChange?.newState === "parking" ||
              (!pendingStateChange && form.operationalState === "parking")
                ? "🅿️"
                : "🔧"}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {pendingStateChange?.newState === "parking" ||
                (!pendingStateChange && form.operationalState === "parking")
                  ? "Stationner le véhicule"
                  : "Envoyer au garage"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pendingStateChange && (
                  <strong>
                    {pendingStateChange.vehicle.plate} -{" "}
                    {pendingStateChange.vehicle.brand}{" "}
                    {pendingStateChange.vehicle.model}
                  </strong>
                )}
                {!pendingStateChange &&
                  (form.operationalState === "parking"
                    ? "Voulez-vous le stationner chez un garage partenaire ?"
                    : "Sélectionnez le garage partenaire pour la maintenance")}
              </Typography>
            </Box>
            </Box>
            {isMobile && (
              <IconButton onClick={() => setShowGarageDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {/* Motif et date estimée */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label={
                  pendingStateChange?.newState === "parking" ||
                  (!pendingStateChange && form.operationalState === "parking")
                    ? "Motif du stationnement"
                    : "Motif de la maintenance"
                }
                placeholder={
                  pendingStateChange?.newState === "parking" ||
                  (!pendingStateChange && form.operationalState === "parking")
                    ? "Ex: En attente d'affectation, Stockage..."
                    : "Ex: Révision, Réparation freins..."
                }
                value={garageForm.maintenanceReason}
                onChange={(e) =>
                  setGarageForm({
                    ...garageForm,
                    maintenanceReason: e.target.value,
                  })
                }
                size="small"
              />
              <DatePicker
                label={
                  pendingStateChange?.newState === "parking" ||
                  (!pendingStateChange && form.operationalState === "parking")
                    ? "Date de récupération prévue"
                    : "Sortie estimée"
                }
                value={
                  garageForm.estimatedExitDate
                    ? dayjs(garageForm.estimatedExitDate)
                    : null
                }
                onChange={(date) =>
                  setGarageForm({
                    ...garageForm,
                    estimatedExitDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 180 } },
                }}
              />
            </Box>

            {/* Liste des garages */}
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {garages.length} garage{garages.length > 1 ? "s" : ""} partenaire
              {garages.length > 1 ? "s" : ""}
            </Typography>

            {garages.length === 0 ? (
              <Alert severity="info">
                Aucun garage partenaire enregistré. Ajoutez-en depuis la page
                Garages.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                  gap: 2,
                }}
              >
                {garages.map((garage) => (
                  <Box
                    key={garage._id}
                    onClick={() => setSelectedGarage(garage)}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border:
                        selectedGarage?._id === garage._id
                          ? "2px solid #F59E0B"
                          : "1px solid #E5E7EB",
                      bgcolor:
                        selectedGarage?._id === garage._id
                          ? "#FFFBEB"
                          : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "#F59E0B",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {garage.nomCommercial}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {garage.adresse}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {garage.ville}
                        </Typography>
                      </Box>
                      {garage.scoreClassement && (
                        <Chip
                          label={garage.scoreClassement}
                          size="small"
                          sx={{
                            bgcolor:
                              garage.scoreClassement === "A"
                                ? "#22C55E"
                                : garage.scoreClassement === "B"
                                ? "#F59E0B"
                                : "#EF4444",
                            color: "white",
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        📞 {garage.telephone}
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color="primary.main"
                      >
                        {garage.tauxHoraireActuel}€/h
                      </Typography>
                    </Box>
                    {selectedGarage?._id === garage._id && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "#F59E0B",
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight={600}>
                          Sélectionné
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: "1px solid #E5E7EB" }}>
            <Button
              onClick={() => {
                setShowGarageDialog(false);
                setSelectedGarage(null);
                setPendingStateChange(null);
                // Si on annule et qu'on était dans le formulaire, on remet l'état précédent
                if (!pendingStateChange && !selectedGarage) {
                  setForm((prev) => ({
                    ...prev,
                    operationalState: "disponible",
                  }));
                }
              }}
            >
              Annuler
            </Button>
            {(pendingStateChange?.newState === "parking" ||
              (!pendingStateChange && form.operationalState === "parking")) && (
              <Button
                variant="outlined"
                onClick={async () => {
                  if (pendingStateChange) {
                    // Changement depuis le tableau - sans garage
                    try {
                      await axios.patch(
                        `${API_URL}/vehicles/${pendingStateChange.vehicle.plate}/operational-state`,
                        {
                          operationalState: pendingStateChange.newState,
                          currentGarage: undefined,
                        }
                      );
                      fetchVehicles();
                    } catch (error) {
                      console.error("Erreur:", error);
                    }
                  } else {
                    // Depuis le formulaire - sans garage
                    setForm((prev) => ({
                      ...prev,
                      currentGarage: undefined,
                    }));
                  }
                  setSelectedGarage(null);
                  setPendingStateChange(null);
                  setShowGarageDialog(false);
                }}
                sx={{
                  borderColor: "#94A3B8",
                  color: "#64748B",
                  "&:hover": { borderColor: "#64748B", bgcolor: "#F1F5F9" },
                  borderRadius: 2,
                }}
              >
                Sans garage
              </Button>
            )}
            <Button
              variant="contained"
              disabled={!selectedGarage}
              onClick={async () => {
                if (selectedGarage) {
                  const garageData = {
                    garageId: selectedGarage._id,
                    garageName: selectedGarage.nomCommercial,
                    garageAddress: `${selectedGarage.adresse}, ${selectedGarage.ville}`,
                    garagePhone: selectedGarage.telephone,
                    entryDate: new Date().toISOString(),
                    estimatedExitDate: garageForm.estimatedExitDate,
                    maintenanceReason: garageForm.maintenanceReason,
                  };

                  if (pendingStateChange) {
                    // Changement depuis le tableau
                    try {
                      await axios.patch(
                        `${API_URL}/vehicles/${pendingStateChange.vehicle.plate}/operational-state`,
                        {
                          operationalState: pendingStateChange.newState,
                          currentGarage: garageData,
                        }
                      );
                      fetchVehicles();
                    } catch (error) {
                      console.error("Erreur:", error);
                    }
                  } else {
                    // Depuis le formulaire
                    setForm((prev) => ({
                      ...prev,
                      currentGarage: garageData,
                    }));
                  }
                  setPendingStateChange(null);
                  setShowGarageDialog(false);
                }
              }}
              sx={{
                bgcolor:
                  pendingStateChange?.newState === "parking" ||
                  (!pendingStateChange && form.operationalState === "parking")
                    ? "#0EA5E9"
                    : "#F59E0B",
                "&:hover": {
                  bgcolor:
                    pendingStateChange?.newState === "parking" ||
                    (!pendingStateChange && form.operationalState === "parking")
                      ? "#0284C7"
                      : "#D97706",
                },
                px: 3,
                borderRadius: 2,
              }}
            >
              {pendingStateChange?.newState === "parking" ||
              (!pendingStateChange && form.operationalState === "parking")
                ? "Stationner chez ce garage"
                : "Confirmer l'envoi au garage"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* QR Code Dialog */}
        {qrVehicle && (
          <VehicleQRCode
            plate={qrVehicle.plate}
            brand={qrVehicle.brand}
            model={qrVehicle.model}
            open={!!qrVehicle}
            onClose={() => setQrVehicle(null)}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default VehiclesPage;
